import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  Code2,
  DatabaseZap,
  Download,
  Eye,
  FileJson,
  FileSearch,
  Layers3,
  LayoutDashboard,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wand2,
  X,
} from 'lucide-react';
import { apiFetch, ApiError } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';

interface WorkbooksTabProps {
  customerId?: string;
  customerName?: string;
}

type WorkbookSourceType = 'oob' | 'custom' | 'imported' | 'ai_generated';
type WorkbookSection = 'catalog' | 'oob' | 'custom' | 'studio' | 'deployments' | 'generator';

interface WorkbookSummaryResponse {
  ok: boolean;
  phase: string;
  status: string;
  stats: {
    catalogCount: number;
    oobCount?: number;
    customCount?: number;
    deploymentsCount: number;
    syncRunsCount: number;
    auditEventsCount: number;
    validationWarningCount?: number;
  };
  sources?: {
    oobRoots: string[];
    customRoots: string[];
    maxFiles: number;
    maxFileBytes: number;
  };
  tabs: Array<{ id: string; label: string; status: string }>;
  nextSteps: string[];
}

interface WorkbookCatalogItem {
  id: string;
  workbookKey: string;
  displayName: string;
  description: string;
  category: string;
  sourceType: WorkbookSourceType;
  sourcePath: string;
  sourceRelativePath?: string;
  sourceRepo: string;
  solutionName?: string;
  packName?: string;
  requiredTables: string[];
  requiredConnectors: string[];
  mitreTactics: string[];
  mitreTechniques: string[];
  tags: string[];
  validationStatus?: string;
  queryCount?: number;
  runnableStatus?: string;
  missingTables?: string[];
  tableStatus?: Array<{ table: string; present: boolean; lastSeen?: string; events?: number }>;
  readinessStatus?: string;
  warningBuckets?: string[];
  updatedAt: string;
}

interface WorkbookCatalogResponse {
  ok: boolean;
  phase: string;
  catalog: WorkbookCatalogItem[];
}

interface WorkbookDeploymentItem {
  id: string;
  workbookId: string;
  customerId: string;
  tenantId?: string;
  subscriptionId?: string;
  resourceGroup: string;
  workspaceName: string;
  azureResourceId?: string;
  deploymentStatus: string;
  deploymentName?: string;
  deploymentMode?: string;
  location?: string;
  deployedByEmail: string;
  deployedAt: string;
  updatedAt?: string;
  rollbackAvailable?: boolean;
  lastError: string;
}

interface WorkbookDeploymentsResponse {
  ok: boolean;
  deployments: WorkbookDeploymentItem[];
}

interface WorkbookSyncRunItem {
  id: string;
  sourceType: string;
  status: string;
  filesScanned: number;
  insertedCount: number;
  updatedCount: number;
  unchangedCount?: number;
  skippedCount: number;
  validationErrors?: Array<{ file?: string; message: string }>;
  startedByEmail: string;
  startedAt: string;
  finishedAt: string;
}

interface WorkbookSyncRunsResponse {
  ok: boolean;
  runs: WorkbookSyncRunItem[];
}

interface WorkbookActionResponse {
  ok: boolean;
  message?: string;
  summary?: {
    sourceType: string;
    status: string;
    filesScanned: number;
    parsedWorkbooks: number;
    inserted: number;
    updated: number;
    unchanged: number;
    skipped: number;
    validationErrors: Array<{ file?: string; message: string; bucket?: string }>;
    warningBuckets?: Record<string, number>;
  };
}


interface WorkbookQueryBlock {
  id: string;
  title: string;
  query: string;
  requiredTables: string[];
  preview: string;
  characterCount: number;
}

interface WorkbookDetailResponse {
  ok: boolean;
  phase: string;
  workbook: WorkbookCatalogItem & {
    validationErrors?: string[];
    contentHash?: string;
    createdAt?: string;
    createdByEmail?: string;
  };
  version: {
    id: string;
    label: string;
    notes: string;
    createdAt: string;
  };
  jsonMetadata: {
    topLevelKeys?: string[];
    resourceTypes?: string[];
    parameterNames?: string[];
    workbookItemTypes?: string[];
    workbookResourceCount?: number;
    serializedDataPresent?: boolean;
    queryBlockCount?: number;
    visualLikeItemCount?: number;
    sectionLikeItemCount?: number;
  };
  queryBlocks: WorkbookQueryBlock[];
  workbookJson: unknown;
  workbookJsonPreview: string;
  metadata: Record<string, unknown>;
}

interface WorkbookRuntimeQueryResult {
  id: string;
  title: string;
  status: 'rendered' | 'no_data' | 'skipped' | 'failed' | 'missing_tables';
  visualization: 'kpi' | 'bar' | 'trend' | 'table' | 'empty';
  requiredTables: string[];
  missingTables: string[];
  columns: string[];
  rows: unknown[][];
  rowObjects: Record<string, unknown>[];
  rowCount: number;
  totalRowsReturned: number;
  durationMs: number;
  error?: string;
  skippedReason?: string;
  queryPreview: string;
  executedQuery?: string;
  parameterStatus?: string;
  resolvedParameters?: string[];
  defaultedParameters?: string[];
  rewritten?: boolean;
  sourceTables?: string[];
  toleratedMissingTables?: string[];
  partialExecution?: boolean;
  chart?: {
    categoryColumn?: string;
    valueColumn?: string;
    points: Array<{ label: string; value: number }>;
  };
}

interface WorkbookRuntimeParameter {
  name: string;
  label: string;
  type: string;
  source: string;
  selectedValue: unknown;
  defaulted: boolean;
  resolved: boolean;
  options?: string[];
  optionQuery?: string;
}

interface WorkbookRenderPayload {
  mode: string;
  timeRange: string;
  customerId?: string;
  generatedAt: string;
  notice: string;
  parameters?: WorkbookRuntimeParameter[];
  runtime?: {
    liveExecution?: boolean;
    state?: string;
    customerId?: string;
    timeRange?: string;
    timespan?: string;
    maxQueries?: number;
    rowLimit?: number;
    totalQueryBlocks?: number;
    attempted?: number;
    rendered?: number;
    skipped?: number;
    failed?: number;
    noData?: number;
    durationMs?: number;
    workspaceId?: string;
    warning?: string;
    parameterCount?: number;
    defaultedParameterCount?: number;
    unresolvedParameterCount?: number;
  };
  queryResults?: WorkbookRuntimeQueryResult[];
  summaryCards: Array<{ label: string; value: string | number; tone?: string }>;
  visualSections: Array<Record<string, unknown>>;
}

interface WorkbookRenderResponse {
  ok: boolean;
  phase: string;
  renderedPayload: WorkbookRenderPayload;
  detail: WorkbookDetailResponse;
}

interface WorkbookExportResponse {
  ok: boolean;
  filename: string;
  exportedAt: string;
  workbook: WorkbookCatalogItem;
  version: WorkbookDetailResponse['version'];
  jsonMetadata: WorkbookDetailResponse['jsonMetadata'];
  queryBlocks: WorkbookQueryBlock[];
  workbookJson: unknown;
}

interface WorkbookImportResponse {
  ok: boolean;
  importId: string;
  saved: boolean;
  action: string;
  validationStatus: string;
  validationErrors: string[];
  warningBuckets: string[];
  preview: {
    displayName: string;
    description: string;
    category: string;
    sourceType: string;
    requiredTables: string[];
    requiredConnectors: string[];
    mitreTactics: string[];
    mitreTechniques: string[];
    tags: string[];
    queryCount: number;
  };
  message?: string;
}

interface WorkbookAiAnalysisResponse {
  ok: boolean;
  id: string;
  summary: string;
  recommendations: string[];
  model: string;
  generatedAt: string;
}

interface WorkbookParameterHint {
  parameterName: string;
  recommendedValue: string;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  source: string;
  examples?: string[];
}

interface WorkbookParameterHintsResponse {
  ok: boolean;
  phase: string;
  model: string;
  generatedAt: string;
  hints: WorkbookParameterHint[];
}

interface WorkbookParameterOptionsResponse {
  ok: boolean;
  phase: string;
  generatedAt: string;
  options: Record<string, string[]>;
  diagnostics?: Array<{ parameterName: string; status: string; message?: string }>;
}

interface WorkbookCloneResponse {
  ok: boolean;
  message?: string;
  workbookId: string;
  versionId?: string;
  workbook?: { id: string; displayName: string; sourceType: WorkbookSourceType };
}

interface WorkbookPresetResponse {
  ok: boolean;
  id: string;
  presetName?: string;
  viewName?: string;
  customerId?: string;
  parameterValues?: Record<string, unknown>;
  timeRange?: string;
  savedAt?: string;
}

interface WorkbookPdfReportResponse {
  ok: boolean;
  filename: string;
  html: string;
  generatedAt: string;
}

interface WorkbookGeneratedQuery {
  id: string;
  title: string;
  purpose: string;
  table: string;
  kql: string;
  visualization: 'kpi' | 'bar' | 'trend' | 'table';
  expectedColumns: string[];
}

interface WorkbookGeneratedDraft {
  displayName: string;
  description: string;
  category: string;
  sourceType: 'ai_generated';
  requiredTables: string[];
  requiredConnectors: string[];
  mitreTactics: string[];
  mitreTechniques: string[];
  tags: string[];
  kqlBlocks: WorkbookGeneratedQuery[];
  layout: Array<Record<string, unknown>>;
  workbookJson: unknown;
  workbookJsonPreview: string;
  explanation: string;
  improvements: string[];
  readinessNotes: string[];
}

interface WorkbookGenerateResponse {
  ok: boolean;
  id: string;
  phase: string;
  model: string;
  aiProvider: string;
  generatedAt: string;
  saved: boolean;
  workbookId?: string | null;
  action?: string | null;
  draft: WorkbookGeneratedDraft;
}

interface WorkbookDeploymentPlanResponse {
  ok: boolean;
  plannedOnly?: boolean;
  message?: string;
  planId?: string;
  context: Record<string, unknown>;
  validation: { ok: boolean; errors: string[]; warnings: string[] };
  armTemplate?: Record<string, unknown>;
  parametersJson?: Record<string, unknown>;
  armPayload?: Record<string, unknown>;
  resourceId?: string;
  deploymentId?: string;
  status?: number;
  response?: string;
}

interface CustomerOption {
  id: string;
  name: string;
  tenantId?: string;
  subscription?: string;
  resourceGroup?: string;
  workspaceId?: string;
  workspaceName?: string;
}

const sections: Array<{ id: WorkbookSection; label: string; subtitle: string }> = [
  { id: 'catalog', label: 'Catalog', subtitle: 'Unified workbook inventory' },
  { id: 'oob', label: 'OOB Sentinel', subtitle: 'Local Azure-Sentinel sync' },
  { id: 'custom', label: 'Custom', subtitle: 'MSSP workbook packs' },
  { id: 'studio', label: 'Interactive Studio', subtitle: 'Enhanced workbook window' },
  { id: 'deployments', label: 'Deployments', subtitle: 'Customer rollout tracker' },
  { id: 'generator', label: 'AI Generator', subtitle: 'Prompt-to-workbook roadmap' },
];

const roadmapCards = [
  {
    title: 'OOB Sentinel Workbook Library',
    sourceType: 'oob' as const,
    category: 'Microsoft Sentinel',
    description: 'Run OOB sync after cloning or reusing the local Azure-Sentinel repository cache. Patch 2 will catalog real workbook JSON from Workbooks and Solutions folders.',
    requiredTables: ['SecurityIncident', 'SecurityAlert', 'SigninLogs'],
    tags: ['repo-sync', 'validation', 'catalog'],
  },
  {
    title: 'Defender XDR Custom Workbook Starter Pack',
    sourceType: 'custom' as const,
    category: 'Defender XDR',
    description: 'Run Custom sync after cloning johnB007/Defender_XDR into the configured custom workbook folder. These templates will seed the starter custom catalog.',
    requiredTables: ['DeviceEvents', 'DeviceInfo', 'AlertInfo'],
    tags: ['custom', 'defender-xdr', 'starter-pack'],
  },
  {
    title: 'Interactive Workbook Studio',
    sourceType: 'ai_generated' as const,
    category: 'Studio',
    description: 'A richer KloudSecure view will wrap workbook JSON with cards, visual sections, theme-aware layout, AI explanations, and export options in a later patch.',
    requiredTables: ['Dynamic per workbook'],
    tags: ['studio', 'preview', 'ai-summary'],
  },
];

function sourceBadge(source: string): string {
  if (source === 'oob') return 'OOB';
  if (source === 'custom') return 'Custom';
  if (source === 'imported') return 'Imported';
  if (source === 'ai_generated') return 'AI Generated';
  return source;
}

function sourceBadgeClass(source: string): string {
  if (source === 'oob') return 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-400/30 dark:bg-blue-500/10 dark:text-blue-100';
  if (source === 'custom') return 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-400/30 dark:bg-cyan-500/10 dark:text-cyan-100';
  if (source === 'imported') return 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-400/30 dark:bg-violet-500/10 dark:text-violet-100';
  if (source === 'ai_generated') return 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-400/30 dark:bg-fuchsia-500/10 dark:text-fuchsia-100';
  return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';
}

const FIXED_SOURCE_FILTER_OPTIONS = [
  { value: 'oob', label: 'OOB' },
  { value: 'custom', label: 'Custom' },
  { value: 'imported', label: 'Imported' },
  { value: 'ai_generated', label: 'AI Generated' },
];

function validationClass(status?: string): string {
  if (status === 'valid') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100';
  if (status === 'warning') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100';
  if (status === 'invalid') return 'border-red-300 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-100';
  return 'border-slate-300 bg-slate-100 text-muted-foreground dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400';
}

function runnableLabel(status?: string): string {
  if (status === 'runnable') return 'Runnable';
  if (status === 'missing_tables') return 'Missing Tables';
  if (status === 'unknown_data_readiness') return 'Readiness Unknown';
  if (status === 'no_tables_detected') return 'No Tables Detected';
  if (status === 'invalid') return 'Invalid';
  if (status === 'warning') return 'Needs Review';
  return 'Not checked';
}

function runnableClass(status?: string): string {
  if (status === 'runnable') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100';
  if (status === 'missing_tables') return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400/40 dark:bg-orange-500/10 dark:text-orange-100';
  if (status === 'unknown_data_readiness') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100';
  if (status === 'no_tables_detected') return 'border-slate-300 bg-slate-100 text-muted-foreground dark:border-slate-600 dark:bg-slate-950 dark:text-slate-300';
  if (status === 'invalid') return 'border-red-300 bg-red-50 text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-100';
  return 'border-slate-300 bg-slate-100 text-muted-foreground dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400';
}

function tableChipClass(table: string, item: WorkbookCatalogItem): string {
  if ((item.missingTables ?? []).includes(table)) return 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-400/40 dark:bg-orange-500/10 dark:text-orange-100';
  if (item.runnableStatus === 'runnable') return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-100';
  return 'border-slate-300 bg-slate-100 text-muted-foreground dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300';
}

function formatDate(value?: string): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function uniqueStringValues(values: string[], limit = 100): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const clean = String(value ?? '').trim();
    if (!clean || seen.has(clean)) continue;
    seen.add(clean);
    out.push(clean);
    if (out.length >= limit) break;
  }
  return out;
}

export function WorkbooksTab({ customerId, customerName }: WorkbooksTabProps) {
  const { canCapability } = useAuth();
  const [activeSection, setActiveSection] = useState<WorkbookSection>('catalog');
  const [summary, setSummary] = useState<WorkbookSummaryResponse | null>(null);
  const [catalog, setCatalog] = useState<WorkbookCatalogItem[]>([]);
  const [deployments, setDeployments] = useState<WorkbookDeploymentItem[]>([]);
  const [syncRuns, setSyncRuns] = useState<WorkbookSyncRunItem[]>([]);
  const [search, setSearch] = useState('');
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [validationFilters, setValidationFilters] = useState<string[]>([]);
  const [readinessFilters, setReadinessFilters] = useState<string[]>([]);
  const [tableFilters, setTableFilters] = useState<string[]>([]);
  const [connectorFilters, setConnectorFilters] = useState<string[]>([]);
  const [tacticFilters, setTacticFilters] = useState<string[]>([]);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [generatorPrompt, setGeneratorPrompt] = useState('Create a customer executive workbook covering incidents, SLA, connector health, threat intelligence hits and top risky entities.');
  const [generatorMode, setGeneratorMode] = useState('executive_soc');
  const [generatorTables, setGeneratorTables] = useState('');
  const [generatorResult, setGeneratorResult] = useState<WorkbookGenerateResponse | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<WorkbookDetailResponse | null>(null);
  const [studioPayload, setStudioPayload] = useState<WorkbookRenderPayload | null>(null);
  const detailsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [studioMode, setStudioMode] = useState<'preview' | 'json_template' | 'enhanced'>('enhanced');
  const [timeRange, setTimeRange] = useState('24h');
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [importFilename, setImportFilename] = useState('custom-workbook.json');
  const [importText, setImportText] = useState('');
  const [importResult, setImportResult] = useState<WorkbookImportResponse | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<WorkbookAiAnalysisResponse | null>(null);
  const [deploymentWorkbookId, setDeploymentWorkbookId] = useState('');
  const [deploymentCustomerId, setDeploymentCustomerId] = useState(customerId ?? '');
  const [studioCustomerId, setStudioCustomerId] = useState(customerId ?? '');
  const [deploymentSubscriptionId, setDeploymentSubscriptionId] = useState('');
  const [deploymentResourceGroup, setDeploymentResourceGroup] = useState('');
  const [deploymentWorkspaceName, setDeploymentWorkspaceName] = useState('');
  const [deploymentLocation, setDeploymentLocation] = useState('southeastasia');
  const [deploymentName, setDeploymentName] = useState('');
  const [deploymentEnableAi, setDeploymentEnableAi] = useState(false);
  const [deploymentConfirm, setDeploymentConfirm] = useState(false);
  const [deploymentPlan, setDeploymentPlan] = useState<WorkbookDeploymentPlanResponse | null>(null);
  const [parameterOverrides, setParameterOverrides] = useState<Record<string, string>>({});
  const [parameterHints, setParameterHints] = useState<Record<string, WorkbookParameterHint>>({});
  const [parameterOptions, setParameterOptions] = useState<Record<string, string[]>>({});
  const [showParameterEditor, setShowParameterEditor] = useState(true);
  const [fullscreenActive, setFullscreenActive] = useState(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(new URLSearchParams(window.location.search).get('workbookFullscreenId'));
  });
  const [fullscreenTab, setFullscreenTab] = useState<'overview' | 'visuals' | 'parameters' | 'queries' | 'json'>('overview');


  const canRead = canCapability('workbooks', 'read');
  const canSyncOob = canCapability('workbooks', 'sync_oob');
  const canSyncCustom = canCapability('workbooks', 'sync_custom');
  const canPreview = canCapability('workbooks', 'preview');
  const canOpenInteractive = canCapability('workbooks', 'open_interactive');
  const canExport = canCapability('workbooks', 'export');
  const canDeploy = canCapability('workbooks', 'deploy');
  const canImport = canCapability('workbooks', 'import');
  const canRollback = canCapability('workbooks', 'rollback');
  const canGenerateAi = canCapability('workbooks', 'generate_ai');

  const optionValues = useMemo(() => {
    const values = {
      sources: new Set<string>(),
      validations: new Set<string>(),
      readiness: new Set<string>(),
      tables: new Set<string>(),
      connectors: new Set<string>(),
      tactics: new Set<string>(),
      categories: new Set<string>(),
    };
    for (const row of catalog) {
      values.sources.add(row.sourceType);
      if (row.validationStatus) values.validations.add(row.validationStatus);
      if (row.runnableStatus) values.readiness.add(row.runnableStatus);
      if (row.category) values.categories.add(row.category);
      row.requiredTables.forEach((v) => values.tables.add(v));
      row.requiredConnectors.forEach((v) => values.connectors.add(v));
      row.mitreTactics.forEach((v) => values.tactics.add(v));
    }
    const sorted = (set: Set<string>) => Array.from(set).filter(Boolean).sort((a, b) => a.localeCompare(b));
    return {
      sources: FIXED_SOURCE_FILTER_OPTIONS.filter((option) => values.sources.has(option.value) || ['oob', 'custom', 'imported', 'ai_generated'].includes(option.value)),
      validations: sorted(values.validations).map((value) => ({ value, label: value })),
      readiness: sorted(values.readiness).map((value) => ({ value, label: runnableLabel(value) })),
      tables: sorted(values.tables).slice(0, 120).map((value) => ({ value, label: value })),
      connectors: sorted(values.connectors).map((value) => ({ value, label: value })),
      tactics: sorted(values.tactics).map((value) => ({ value, label: value })),
      categories: sorted(values.categories).map((value) => ({ value, label: value })),
    };
  }, [catalog]);

  const visibleCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    const hasAny = (selected: string[], values: string[]) => selected.length === 0 || selected.some((s) => values.includes(s));
    return catalog.filter((row) => {
      if (!hasAny(sourceFilters, [row.sourceType])) return false;
      if (!hasAny(validationFilters, [row.validationStatus ?? 'unknown'])) return false;
      if (!hasAny(readinessFilters, [row.runnableStatus ?? 'not_checked'])) return false;
      if (!hasAny(tableFilters, row.requiredTables)) return false;
      if (!hasAny(connectorFilters, row.requiredConnectors)) return false;
      if (!hasAny(tacticFilters, row.mitreTactics)) return false;
      if (!hasAny(categoryFilters, [row.category])) return false;
      if (!q) return true;
      return [
        row.displayName,
        row.description,
        row.category,
        row.sourceRelativePath ?? '',
        row.solutionName ?? '',
        row.packName ?? '',
        (row.missingTables ?? []).join(' '),
        row.requiredTables.join(' '),
        row.requiredConnectors.join(' '),
        row.mitreTactics.join(' '),
        row.mitreTechniques.join(' '),
        row.tags.join(' '),
      ].join(' ').toLowerCase().includes(q);
    });
  }, [catalog, search, sourceFilters, validationFilters, readinessFilters, tableFilters, connectorFilters, tacticFilters, categoryFilters]);

  const customAndImportedWorkbooks = useMemo(() => catalog.filter((row) => row.sourceType === 'custom' || row.sourceType === 'imported'), [catalog]);

  async function refreshAll(): Promise<void> {
    if (!canRead) return;
    setLoading(true);
    setError('');
    try {
      const [summaryResponse, catalogResponse, deploymentResponse, syncResponse] = await Promise.all([
        apiFetch<WorkbookSummaryResponse>('/workbooks/summary'),
        apiFetch<WorkbookCatalogResponse>(`/workbooks/catalog${customerId ? `?customerId=${encodeURIComponent(customerId)}` : ''}`),
        apiFetch<WorkbookDeploymentsResponse>('/workbooks/deployments'),
        apiFetch<WorkbookSyncRunsResponse>('/workbooks/sync-runs'),
      ]);
      setSummary(summaryResponse);
      setCatalog(catalogResponse.catalog ?? []);
      setDeployments(deploymentResponse.deployments ?? []);
      setSyncRuns(syncResponse.runs ?? []);
      try {
        const customersResponse = await apiFetch<{ customers: CustomerOption[] }>('/customers');
        setCustomers(customersResponse.customers ?? []);
      } catch {
        setCustomers(customerId ? [{ id: customerId, name: customerName ?? customerId }] : []);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to load Workbooks module.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshAll();
  }, [canRead, customerId]);

  useEffect(() => {
    if (customerId) setDeploymentCustomerId(customerId);
  }, [customerId]);

  useEffect(() => {
    const selected = customers.find((c) => c.id === deploymentCustomerId);
    if (!selected) return;
    if (!deploymentSubscriptionId && selected.subscription) setDeploymentSubscriptionId(selected.subscription);
    if (!deploymentResourceGroup && selected.resourceGroup) setDeploymentResourceGroup(selected.resourceGroup);
    if (!deploymentWorkspaceName && selected.workspaceName) setDeploymentWorkspaceName(selected.workspaceName);
  }, [customers, deploymentCustomerId]);

  useEffect(() => {
    if (!studioCustomerId && customerId) setStudioCustomerId(customerId);
  }, [customerId, studioCustomerId]);

  useEffect(() => {
    const selected = catalog.find((row) => row.id === deploymentWorkbookId);
    if (selected && !deploymentName) setDeploymentName(`ks-${selected.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60));
  }, [catalog, deploymentWorkbookId]);

  async function runAction(endpoint: string, busyKey: string, successPrefix: string, body?: unknown): Promise<void> {
    setActionBusy(busyKey);
    setError('');
    setMessage('');
    try {
      const response = await apiFetch<WorkbookActionResponse>(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });
      const bucketText = response.summary?.warningBuckets && Object.keys(response.summary.warningBuckets).length > 0
        ? ` Buckets: ${Object.entries(response.summary.warningBuckets).map(([key, value]) => `${key}=${value}`).join(', ')}.`
        : '';
      const detail = response.summary
        ? ` Scanned ${response.summary.filesScanned}, inserted ${response.summary.inserted}, updated ${response.summary.updated}, unchanged ${response.summary.unchanged}, skipped ${response.summary.skipped}.${bucketText}`
        : '';
      setMessage(response.message ? `${successPrefix}: ${response.message}` : `${successPrefix}.${detail}`);
      await refreshAll();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Action failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  function downloadTextFile(filename: string, content: string, mime = 'text/plain'): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function exportCatalogCsv(): void {
    const headers = ['Display Name', 'Source', 'Category', 'Validation', 'Readiness', 'Required Tables', 'Missing Tables', 'Connectors', 'MITRE Tactics', 'Tags', 'Source Path', 'Updated At'];
    const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = visibleCatalog.map((row) => [
      row.displayName,
      sourceBadge(row.sourceType),
      row.category,
      row.validationStatus ?? '',
      runnableLabel(row.runnableStatus),
      row.requiredTables.join('; '),
      (row.missingTables ?? []).join('; '),
      row.requiredConnectors.join('; '),
      row.mitreTactics.join('; '),
      row.tags.join('; '),
      row.sourceRelativePath || row.sourcePath,
      row.updatedAt,
    ]);
    downloadTextFile(`kloudsecure-workbooks-catalog-${new Date().toISOString().slice(0, 10)}.csv`, [headers, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n'), 'text/csv');
    setMessage(`Exported ${visibleCatalog.length} workbook catalog row(s) to CSV.`);
  }

  async function handleImportFile(file: File | null): Promise<void> {
    if (!file) return;
    setImportFilename(file.name);
    setImportText(await file.text());
    setImportResult(null);
  }

  async function validateOrSaveImport(save: boolean): Promise<void> {
    if (!importText.trim()) {
      setError('Paste or upload workbook JSON before importing.');
      return;
    }
    setActionBusy(save ? 'import-save' : 'import-validate');
    setError('');
    setMessage('');
    try {
      const response = await apiFetch<WorkbookImportResponse>('/workbooks/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: importFilename, content: importText, save }),
      });
      setImportResult(response);
      setMessage(response.message ?? (save ? 'Imported workbook saved.' : 'Workbook JSON validated.'));
      if (save) await refreshAll();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Import failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function runWorkbookAiAnalysis(itemOrDetail?: WorkbookCatalogItem | WorkbookDetailResponse): Promise<void> {
    const workbookId = itemOrDetail ? ('workbook' in itemOrDetail ? itemOrDetail.workbook.id : itemOrDetail.id) : selectedDetail?.workbook.id;
    if (!workbookId) {
      setMessage('Select a workbook before running AI Analysis.');
      return;
    }
    setActionBusy(`ai-analysis-${workbookId}`);
    setError('');
    try {
      const response = await apiFetch<WorkbookAiAnalysisResponse>(`/workbooks/${encodeURIComponent(workbookId)}/ai-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: studioCustomerId || customerId || deploymentCustomerId, customerName: customers.find((c) => c.id === (studioCustomerId || customerId || deploymentCustomerId))?.name ?? customerName ?? '', enableAiAnalysis: deploymentEnableAi }),
      });
      setAiAnalysis(response);
      setMessage('Workbook AI Analysis completed.');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'AI Analysis failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  function deploymentBody(extra: Record<string, unknown> = {}): Record<string, unknown> {
    const selectedCustomer = customers.find((c) => c.id === deploymentCustomerId);
    return {
      customerId: deploymentCustomerId,
      customerName: selectedCustomer?.name ?? customerName ?? '',
      subscriptionId: deploymentSubscriptionId,
      resourceGroup: deploymentResourceGroup,
      workspaceName: deploymentWorkspaceName,
      location: deploymentLocation,
      deploymentName,
      enableAiAnalysis: deploymentEnableAi,
      deploymentMode: 'deploy_to_azure',
      ...extra,
    };
  }

  async function generateDeploymentPlan(): Promise<void> {
    if (!deploymentWorkbookId) {
      setError('Select a workbook for deployment.');
      return;
    }
    setActionBusy('deployment-plan');
    setError('');
    try {
      const response = await apiFetch<WorkbookDeploymentPlanResponse>(`/workbooks/${encodeURIComponent(deploymentWorkbookId)}/deployment-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deploymentBody()),
      });
      setDeploymentPlan(response);
      setMessage(response.validation.ok ? 'Deployment plan generated and ready.' : 'Deployment plan generated with validation blockers.');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to generate deployment plan.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function exportArmPayload(): Promise<void> {
    if (!deploymentWorkbookId) {
      setError('Select a workbook before exporting ARM.');
      return;
    }
    setActionBusy('arm-export');
    setError('');
    try {
      const response = await apiFetch<WorkbookDeploymentPlanResponse>(`/workbooks/${encodeURIComponent(deploymentWorkbookId)}/generate-arm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deploymentBody()),
      });
      setDeploymentPlan(response);
      downloadTextFile(`${deploymentName || 'kloudsecure-workbook'}.arm.json`, JSON.stringify(response.armTemplate ?? {}, null, 2), 'application/json');
      setMessage('ARM payload exported.');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to export ARM payload.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function deployWorkbook(): Promise<void> {
    if (!deploymentWorkbookId) {
      setError('Select a workbook before deployment.');
      return;
    }
    setActionBusy('workbook-deploy');
    setError('');
    try {
      const response = await apiFetch<WorkbookDeploymentPlanResponse>(`/workbooks/${encodeURIComponent(deploymentWorkbookId)}/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deploymentBody({ confirmDeploy: deploymentConfirm })),
      });
      setDeploymentPlan(response);
      setMessage(response.ok ? 'Workbook deployment completed.' : response.message ?? 'Deployment returned a plan or validation result.');
      await refreshAll();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Workbook deployment failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function rollbackDeployment(deploymentId: string): Promise<void> {
    setActionBusy(`rollback-${deploymentId}`);
    setError('');
    try {
      const response = await apiFetch<WorkbookDeploymentPlanResponse>(`/workbooks/deployments/${encodeURIComponent(deploymentId)}/rollback`, { method: 'POST' });
      setMessage(response.ok ? 'Workbook deployment rolled back.' : 'Rollback request completed with warnings.');
      await refreshAll();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Rollback failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }


  async function loadWorkbookDetails(item: WorkbookCatalogItem): Promise<WorkbookDetailResponse | null> {
    if (item.id.startsWith('roadmap-')) {
      setMessage('Run OOB or Custom sync first, then open details for a real workbook catalog record.');
      return null;
    }
    setDetailLoading(true);
    setError('');
    try {
      const scopedCustomerId = studioCustomerId || customerId || '';
      const query = scopedCustomerId ? `?customerId=${encodeURIComponent(scopedCustomerId)}` : '';
      const detail = await apiFetch<WorkbookDetailResponse>(`/workbooks/${encodeURIComponent(item.id)}/details${query}`);
      setSelectedDetail(detail);
      return detail;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to load workbook details.';
      setError(msg);
      return null;
    } finally {
      setDetailLoading(false);
    }
  }

  function generatedWorkbookStub(id: string, displayName: string): WorkbookCatalogItem {
    return {
      id,
      workbookKey: `ai-generated:${id}`,
      displayName: displayName || 'AI Generated Workbook',
      description: 'AI-generated KloudSecure workbook',
      category: 'AI Generated',
      sourceType: 'ai_generated',
      sourcePath: 'ai-generated://runtime',
      sourceRelativePath: 'AI Generated Workbooks',
      sourceRepo: 'KloudSecure AI Workbook Generator',
      solutionName: 'AI Generated Workbooks',
      packName: 'AI Generator',
      requiredTables: [],
      requiredConnectors: [],
      mitreTactics: [],
      mitreTechniques: [],
      tags: ['ai-generated'],
      validationStatus: 'valid',
      queryCount: 0,
      updatedAt: new Date().toISOString(),
    };
  }

  async function openWorkbookIdInStudio(workbookId: string, displayName: string): Promise<void> {
    const detail = await loadWorkbookDetails(generatedWorkbookStub(workbookId, displayName));
    if (!detail) return;
    setParameterOverrides({});
    setParameterHints({});
    setParameterOptions({});
    setActiveSection('studio');
    setStudioMode('enhanced');
    await renderWorkbookStudio(detail, 'enhanced');
  }

  async function openWorkbookPreview(item: WorkbookCatalogItem): Promise<void> {
    const detail = await loadWorkbookDetails(item);
    if (!detail) return;
    if (canOpenInteractive) {
      setActiveSection('studio');
      setStudioMode('preview');
      await renderWorkbookStudio(detail, 'preview');
      return;
    }
    setMessage('Workbook preview loaded in Catalog details. Interactive Studio permission is required for rendered preview mode.');
    window.setTimeout(() => detailsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  async function exportWorkbookJson(itemOrDetail: WorkbookCatalogItem | WorkbookDetailResponse): Promise<void> {
    const workbookId = 'workbook' in itemOrDetail ? itemOrDetail.workbook.id : itemOrDetail.id;
    if (workbookId.startsWith('roadmap-')) {
      setMessage('Export is available after a real workbook has been synced into the catalog.');
      return;
    }
    setActionBusy(`export-${workbookId}`);
    setError('');
    try {
      const response = await apiFetch<WorkbookExportResponse>(`/workbooks/${encodeURIComponent(workbookId)}/export`);
      const filename = response.filename || `${'workbook' in itemOrDetail ? itemOrDetail.workbook.displayName : itemOrDetail.displayName}.json`;
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMessage(`Exported ${filename}.`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to export workbook JSON.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function exportWorkbookPdf(itemOrDetail?: WorkbookCatalogItem | WorkbookDetailResponse): Promise<void> {
    const workbookId = itemOrDetail ? ('workbook' in itemOrDetail ? itemOrDetail.workbook.id : itemOrDetail.id) : selectedDetail?.workbook.id;
    if (!workbookId) {
      setMessage('Select a workbook before exporting PDF.');
      return;
    }
    setActionBusy(`pdf-${workbookId}`);
    setError('');
    try {
      const response = await apiFetch<WorkbookPdfReportResponse>(`/workbooks/${encodeURIComponent(workbookId)}/pdf-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: studioCustomerId || customerId || deploymentCustomerId, renderedPayload: studioPayload ?? undefined }),
      });
      const win = window.open('', '_blank');
      if (!win) {
        downloadTextFile(response.filename || 'kloudsecure-workbook-report.html', response.html, 'text/html');
        setMessage('PDF report HTML downloaded. Open it and choose Print → Save as PDF.');
        return;
      }
      win.document.open();
      win.document.write(response.html);
      win.document.close();
      win.focus();
      window.setTimeout(() => win.print(), 350);
      setMessage('PDF export opened in a print-ready report window. Choose Save as PDF from the browser print dialog.');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'PDF export failed.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }


  async function cloneSelectedWorkbookToCustom(): Promise<void> {
    if (!selectedDetail) {
      setMessage('Select a workbook before cloning it to Custom.');
      return;
    }
    const defaultName = `Copy of ${selectedDetail.workbook.displayName}`;
    const displayName = window.prompt('Clone workbook to Custom catalog as:', defaultName) || defaultName;
    if (!displayName.trim()) return;
    setActionBusy('fullscreen-clone');
    setError('');
    try {
      const response = await apiFetch<WorkbookCloneResponse>(`/workbooks/${encodeURIComponent(selectedDetail.workbook.id)}/clone-custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName, customerId: studioCustomerId || customerId || '' }),
      });
      await refreshAll();
      setMessage(response.message ?? `Workbook cloned to Custom: ${displayName}`);
      if (response.workbookId) await openWorkbookIdInStudio(response.workbookId, displayName);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to clone workbook to Custom.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function saveCurrentParameterPreset(): Promise<void> {
    if (!selectedDetail) {
      setMessage('Select a workbook before saving a parameter preset.');
      return;
    }
    const presetName = window.prompt('Preset name:', `${selectedDetail.workbook.displayName} - ${timeRange}`) || '';
    if (!presetName.trim()) return;
    setActionBusy('fullscreen-save-preset');
    setError('');
    try {
      const response = await apiFetch<WorkbookPresetResponse>(`/workbooks/${encodeURIComponent(selectedDetail.workbook.id)}/parameter-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: studioCustomerId || customerId || '',
          presetName,
          timeRange,
          parameterValues: parameterOverrides,
        }),
      });
      setMessage(`Parameter preset saved: ${response.presetName ?? presetName}`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to save parameter preset.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function saveCurrentCustomerView(): Promise<void> {
    if (!selectedDetail) {
      setMessage('Select a workbook before saving a customer-specific view.');
      return;
    }
    const viewName = window.prompt('Customer view name:', `${selectedDetail.workbook.displayName} - ${customers.find((c) => c.id === (studioCustomerId || customerId))?.name ?? customerName ?? 'Customer'} - ${timeRange}`) || '';
    if (!viewName.trim()) return;
    setActionBusy('fullscreen-save-view');
    setError('');
    try {
      const response = await apiFetch<WorkbookPresetResponse>(`/workbooks/${encodeURIComponent(selectedDetail.workbook.id)}/customer-views`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: studioCustomerId || customerId || '',
          viewName,
          timeRange,
          parameterValues: parameterOverrides,
          renderedPayload: studioPayload ?? {},
        }),
      });
      setMessage(`Customer workbook view saved: ${response.viewName ?? viewName}`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to save customer workbook view.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  function deploySelectedWorkbookFromFullscreen(): void {
    if (!selectedDetail) {
      setMessage('Select a workbook before opening the deployment wizard.');
      return;
    }
    const wb = selectedDetail.workbook;
    setDeploymentWorkbookId(wb.id);
    setDeploymentCustomerId(studioCustomerId || customerId || deploymentCustomerId || '');
    setDeploymentName(`ks-${wb.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60));
    setDeploymentEnableAi(true);
    setFullscreenActive(false);
    setActiveSection('deployments');
    setMessage('Workbook loaded into Deployment Wizard from Fullscreen Studio. Review parameters and confirm before deployment.');
  }

  async function loadWorkbookParameterOptions(detailOverride?: WorkbookDetailResponse, payloadOverride?: WorkbookRenderPayload | null): Promise<void> {
    const detail = detailOverride ?? selectedDetail;
    if (!detail) return;
    const params = payloadOverride?.parameters ?? studioPayload?.parameters ?? [];
    const hasQueryBacked = params.some((p) => p.source === 'kql' || p.optionQuery);
    const hasEmptyOptions = params.some((p) => (p.options ?? []).length === 0);
    if (!hasQueryBacked && !hasEmptyOptions) return;
    try {
      const response = await apiFetch<WorkbookParameterOptionsResponse>(`/workbooks/${encodeURIComponent(detail.workbook.id)}/parameter-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: studioCustomerId || customerId || '',
          timeRange,
          parameters: parameterOverrides,
        }),
      });
      setParameterOptions(response.options ?? {});
    } catch {
      // Option queries are best-effort. Do not block live rendering if a dropdown option query fails.
    }
  }

  async function renderWorkbookStudio(detailOverride?: WorkbookDetailResponse, modeOverride?: 'preview' | 'json_template' | 'enhanced'): Promise<void> {
    const detail = detailOverride ?? selectedDetail;
    if (!detail) {
      setMessage('Select a workbook from the catalog before rendering the Studio preview.');
      return;
    }
    const mode = modeOverride ?? studioMode;
    setActionBusy('studio-render');
    setError('');
    try {
      const response = await apiFetch<WorkbookRenderResponse>(`/workbooks/${encodeURIComponent(detail.workbook.id)}/render-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode,
          timeRange,
          customerId: studioCustomerId || customerId || '',
          customerName: customers.find((c) => c.id === (studioCustomerId || customerId))?.name ?? customerName ?? '',
          parameters: parameterOverrides,
        }),
      });
      setStudioPayload(response.renderedPayload);
      setSelectedDetail(response.detail);
      void loadWorkbookParameterOptions(response.detail, response.renderedPayload);
      setMessage(response.renderedPayload.runtime?.liveExecution ? `Live workbook render completed: ${response.renderedPayload.runtime.rendered ?? 0} rendered, ${response.renderedPayload.runtime.skipped ?? 0} skipped, ${response.renderedPayload.runtime.failed ?? 0} failed.` : 'Workbook metadata preview rendered. Select a customer/workspace context for live KQL execution.');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to render workbook preview.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  function updateParameterOverride(name: string, value: string): void {
    setParameterOverrides((prev) => ({ ...prev, [name]: value }));
  }

  function clearParameterOverride(name: string): void {
    setParameterOverrides((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function applyParameterHint(name: string): void {
    const hint = parameterHints[name];
    if (!hint) return;
    updateParameterOverride(name, hint.recommendedValue);
  }

  function applyAllParameterHints(): void {
    const next: Record<string, string> = { ...parameterOverrides };
    for (const hint of Object.values(parameterHints) as WorkbookParameterHint[]) {
      if (hint.parameterName && hint.recommendedValue !== undefined) next[hint.parameterName] = hint.recommendedValue;
    }
    setParameterOverrides(next);
    setMessage('AI parameter hints applied. Click Run Live Render to execute with the updated values.');
  }


  async function generateAiWorkbook(save = false): Promise<WorkbookGenerateResponse | null> {
    if (!generatorPrompt.trim()) {
      setError('Enter a workbook prompt first.');
      return null;
    }
    setActionBusy(save ? 'generator-save' : 'generator-generate');
    setError('');
    try {
      const selectedCustomer = customers.find((c) => c.id === (studioCustomerId || customerId));
      const tables = generatorTables.split(/[;,\n]/g).map((t) => t.trim()).filter(Boolean);
      const response = await apiFetch<WorkbookGenerateResponse>('/workbooks/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: generatorPrompt,
          mode: generatorMode,
          customerId: studioCustomerId || customerId || '',
          customerName: selectedCustomer?.name ?? customerName ?? '',
          tables,
          save,
        }),
      });
      setGeneratorResult(response);
      if (save) {
        await refreshAll();
        if (response.workbookId) {
          setDeploymentWorkbookId(response.workbookId);
          setMessage(`AI workbook saved to catalog: ${response.draft.displayName}`);
        } else {
          setError('AI workbook was generated but the catalog record ID was not returned. Refresh Catalog and try again.');
        }
      } else {
        setMessage(`AI workbook draft generated: ${response.draft.displayName}. Generated KQL is pre-checked for common TimeGenerated/ago() syntax issues before save/render.`);
      }
      return response;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'AI workbook generation failed.';
      setError(msg);
      return null;
    } finally {
      setActionBusy(null);
    }
  }

  function exportGeneratedWorkbookJson(): void {
    if (!generatorResult?.draft?.workbookJson) {
      setError('Generate a workbook first, then export JSON.');
      return;
    }
    downloadTextFile(`${generatorResult.draft.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'ai-workbook'}.json`, JSON.stringify(generatorResult.draft.workbookJson, null, 2), 'application/json');
  }

  async function openGeneratedWorkbookInStudio(): Promise<void> {
    let result = generatorResult;
    if (!result) result = await generateAiWorkbook(false);
    if (!result) return;
    if (!result.workbookId) result = await generateAiWorkbook(true);
    if (!result?.workbookId) {
      setError('AI-generated workbook could not be saved to the catalog, so Studio cannot open it yet.');
      return;
    }
    await refreshAll();
    await openWorkbookIdInStudio(result.workbookId, result.draft.displayName);
  }

  async function deployGeneratedWorkbook(): Promise<void> {
    let result = generatorResult;
    if (!result) result = await generateAiWorkbook(false);
    if (!result) return;
    if (!result.workbookId) result = await generateAiWorkbook(true);
    if (!result?.workbookId) {
      setError('AI-generated workbook could not be saved to the catalog, so the deployment wizard cannot use it yet.');
      return;
    }
    await refreshAll();
    setDeploymentWorkbookId(result.workbookId);
    setDeploymentName(`ks-${result.draft.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60));
    setDeploymentEnableAi(true);
    setActiveSection('deployments');
    setMessage('AI-generated workbook saved and loaded into the deployment wizard.');
  }

  async function generateParameterHints(): Promise<void> {
    if (!selectedDetail) {
      setMessage('Select a workbook before generating parameter hints.');
      return;
    }
    setActionBusy('parameter-hints');
    setError('');
    try {
      const response = await apiFetch<WorkbookParameterHintsResponse>(`/workbooks/${encodeURIComponent(selectedDetail.workbook.id)}/parameter-hints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: studioCustomerId || customerId || '',
          timeRange,
          parameters: parameterOverrides,
        }),
      });
      const hintMap: Record<string, WorkbookParameterHint> = {};
      for (const hint of response.hints ?? []) hintMap[hint.parameterName] = hint;
      setParameterHints(hintMap);
      setShowParameterEditor(true);
      setMessage(`AI parameter hints generated for ${Object.keys(hintMap).length} workbook parameter(s).`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to generate parameter hints.';
      setError(msg);
    } finally {
      setActionBusy(null);
    }
  }

  async function openWorkbookStudio(item: WorkbookCatalogItem): Promise<void> {
    const detail = await loadWorkbookDetails(item);
    if (!detail) return;
    setParameterOverrides({});
    setParameterHints({});
    setParameterOptions({});
    setActiveSection('studio');
    setStudioMode('enhanced');
    await renderWorkbookStudio(detail, 'enhanced');
  }

  function openWorkbookFullscreen(item: WorkbookCatalogItem): void {
    if (item.id.startsWith('roadmap-')) {
      setMessage('Fullscreen Workbooks are available after a real workbook has been synced into the catalog.');
      return;
    }
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'workbooks');
    url.searchParams.set('workbookFullscreenId', item.id);
    const scopedCustomer = studioCustomerId || customerId || '';
    if (scopedCustomer) url.searchParams.set('customer', scopedCustomer);
    const opened = window.open(url.toString(), '_blank', 'noopener,noreferrer');
    if (!opened) {
      setMessage('Your browser blocked the Fullscreen Studio popup. Allow popups for this site and try again.');
    }
  }

  function closeFullscreenWorkbook(): void {
    setFullscreenActive(false);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('workbookFullscreenId');
      window.history.replaceState({}, '', url.toString());
    }
  }

  async function changeStudioMode(mode: 'preview' | 'json_template' | 'enhanced'): Promise<void> {
    setStudioMode(mode);
    if (selectedDetail && mode !== 'json_template' && canOpenInteractive) {
      await renderWorkbookStudio(selectedDetail, mode);
    }
  }

  useEffect(() => {
    if (!canRead || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const workbookId = params.get('workbookFullscreenId');
    if (!workbookId) return;
    const scopedCustomer = params.get('customer');
    if (scopedCustomer && !studioCustomerId) setStudioCustomerId(scopedCustomer);
    setFullscreenActive(true);
    setStudioMode('enhanced');
    void openWorkbookIdInStudio(workbookId, 'Fullscreen Workbook');
    // Run only once per deep-linked tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const displayCards = visibleCatalog.length ? visibleCatalog : roadmapCards.map((card, index) => ({
    id: `roadmap-${index}`,
    workbookKey: `roadmap-${index}`,
    displayName: card.title,
    description: card.description,
    category: card.category,
    sourceType: card.sourceType,
    sourcePath: 'Patch roadmap placeholder',
    sourceRelativePath: 'Run sync to replace this placeholder with real catalog records',
    sourceRepo: 'KloudSecure planning shell',
    requiredTables: card.requiredTables,
    requiredConnectors: [],
    mitreTactics: [],
    mitreTechniques: [],
    tags: card.tags,
    validationStatus: 'warning',
    queryCount: 0,
    updatedAt: '',
  } satisfies WorkbookCatalogItem));

  if (!canRead) {
    return (
      <div className="rounded-3xl border border-amber-300 bg-amber-50 p-6 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
        <div className="flex items-center gap-3 text-lg font-semibold"><AlertTriangle className="h-5 w-5" /> Workbooks access is not enabled for your role.</div>
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-100/80">Ask a KloudSecure administrator to enable <span className="font-mono">workbooks.read</span> in Masters → Permissions Master.</p>
      </div>
    );
  }

  if (fullscreenActive) {
    return (
      <FullscreenWorkbookStudio
        catalog={catalog}
        selectedDetail={selectedDetail}
        studioPayload={studioPayload}
        activeTab={fullscreenTab}
        timeRange={timeRange}
        customerName={customers.find((c) => c.id === (studioCustomerId || customerId))?.name ?? customerName}
        customers={customers}
        selectedCustomerId={studioCustomerId}
        busy={actionBusy === 'studio-render'}
        parameterOverrides={parameterOverrides}
        parameterHints={parameterHints}
        parameterOptions={parameterOptions}
        canExport={canExport}
        canGenerateAi={canGenerateAi}
        canOpenInteractive={canOpenInteractive}
        onTabChange={setFullscreenTab}
        onClose={closeFullscreenWorkbook}
        onSelectWorkbook={(id) => openWorkbookIdInStudio(id, catalog.find((row) => row.id === id)?.displayName ?? 'Workbook')}
        onCustomerChange={setStudioCustomerId}
        onTimeRangeChange={setTimeRange}
        onRender={() => void renderWorkbookStudio(selectedDetail ?? undefined, 'enhanced')}
        onExport={() => selectedDetail && void exportWorkbookJson(selectedDetail)}
        onExportPdf={() => void exportWorkbookPdf(selectedDetail ?? undefined)}
        onAiAnalysis={() => selectedDetail && void runWorkbookAiAnalysis(selectedDetail)}
        onParameterChange={updateParameterOverride}
        onParameterClear={clearParameterOverride}
        onGenerateParameterHints={generateParameterHints}
        onApplyParameterHint={applyParameterHint}
        onApplyAllParameterHints={applyAllParameterHints}
        onCloneToCustom={cloneSelectedWorkbookToCustom}
        onSavePreset={saveCurrentParameterPreset}
        onSaveView={saveCurrentCustomerView}
        onDeployFromFullscreen={deploySelectedWorkbookFromFullscreen}
        canDeploy={canDeploy}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-[2rem] border border-cyan-400/20 bg-card p-6 shadow-2xl shadow-cyan-950/20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(32,191,239,0.28),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.22),transparent_32%)]" />
        <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-900 dark:text-cyan-100">
              <BookOpenCheck className="h-4 w-4" /> HuntingOps / Workbooks
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-foreground md:text-4xl">Workbook Command Center</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground md:text-base">
              Patch 6.1 hardens AI-generated workbook save/open actions and repairs common generated KQL syntax before live render, while preserving the Patch 5.4 runtime visual style.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border border-border bg-card px-3 py-1">Customer scope: {customerName ?? 'MSSP / global catalog'}</span>
              <span className="rounded-full border border-border bg-card px-3 py-1">Phase: {summary?.phase ?? 'phase6_1_generator_save_kql_guardrails_hotfix'}</span>
              <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-emerald-800 dark:text-emerald-100">RBAC guarded</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricCard icon={<Layers3 className="h-4 w-4" />} label="Catalog" value={summary?.stats.catalogCount ?? 0} />
            <MetricCard icon={<DatabaseZap className="h-4 w-4" />} label="OOB" value={summary?.stats.oobCount ?? 0} />
            <MetricCard icon={<UploadCloud className="h-4 w-4" />} label="Custom" value={summary?.stats.customCount ?? 0} />
            <MetricCard icon={<ShieldCheck className="h-4 w-4" />} label="Warnings" value={summary?.stats.validationWarningCount ?? 0} />
          </div>
        </div>
      </div>

      {(error || message) && (
        <div className={`rounded-2xl border p-4 text-sm ${error ? 'border-red-500/30 bg-red-500/10 text-red-800 dark:text-red-100' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100'}`}>
          {error || message}
        </div>
      )}

      {aiAnalysis && (
        <div className="rounded-3xl border border-fuchsia-400/20 bg-fuchsia-500/10 p-5 text-sm text-fuchsia-50">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-bold text-foreground">Workbook AI Analysis</div>
              <p className="mt-1 text-fuchsia-900 dark:text-fuchsia-100/90">{aiAnalysis.summary}</p>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {aiAnalysis.recommendations.map((rec) => <div key={rec} className="rounded-xl border border-fuchsia-400/20 bg-card p-3">{rec}</div>)}
              </div>
            </div>
            <button onClick={() => setAiAnalysis(null)} className="rounded-xl border border-fuchsia-400/20 bg-card p-2"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:border-cyan-300/50 ${activeSection === section.id ? 'border-cyan-300/60 bg-cyan-500/15 shadow-lg shadow-cyan-950/30' : 'border-border bg-card'}`}
          >
            <div className="text-sm font-bold text-foreground">{section.label}</div>
            <div className="mt-1 text-xs text-muted-foreground">{section.subtitle}</div>
          </button>
        ))}
      </div>

      {activeSection === 'catalog' && (
        <section className="rounded-3xl border border-border bg-card p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-xl font-bold text-foreground">Workbook Catalog</h3>
              <p className="mt-1 text-sm text-muted-foreground">Search and filter real OOB/custom workbook records after sync. Empty state shows starter roadmap cards.</p>
            </div>
            <div className="flex flex-col gap-2 xl:items-end">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, table, connector, MITRE" className="h-10 w-full rounded-xl border border-border bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-cyan-300 sm:w-72" />
                </div>
                <MultiSelectFilter label="Sources" options={optionValues.sources} selected={sourceFilters} onChange={setSourceFilters} />
                <MultiSelectFilter label="Validation" options={optionValues.validations} selected={validationFilters} onChange={setValidationFilters} />
                <MultiSelectFilter label="Readiness" options={optionValues.readiness} selected={readinessFilters} onChange={setReadinessFilters} />
                <button onClick={() => void refreshAll()} disabled={loading} className="inline-flex h-10 items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-60">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
                <button onClick={exportCatalogCsv} className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 text-sm font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20">
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                <MultiSelectFilter label="Tables" options={optionValues.tables} selected={tableFilters} onChange={setTableFilters} wide />
                <MultiSelectFilter label="Connectors" options={optionValues.connectors} selected={connectorFilters} onChange={setConnectorFilters} wide />
                <MultiSelectFilter label="MITRE tactics" options={optionValues.tactics} selected={tacticFilters} onChange={setTacticFilters} wide />
                <MultiSelectFilter label="Categories" options={optionValues.categories} selected={categoryFilters} onChange={setCategoryFilters} wide />
              </div>
              {(sourceFilters.length + validationFilters.length + readinessFilters.length + tableFilters.length + connectorFilters.length + tacticFilters.length + categoryFilters.length) > 0 && (
                <button onClick={() => { setSourceFilters([]); setValidationFilters([]); setReadinessFilters([]); setTableFilters([]); setConnectorFilters([]); setTacticFilters([]); setCategoryFilters([]); }} className="text-xs font-semibold text-cyan-900 dark:text-cyan-200 hover:text-cyan-900 dark:text-cyan-100">Clear all filters</button>
              )}
            </div>
          </div>

          <div ref={detailsAnchorRef} className="mt-5" />
          {detailLoading && <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4 text-sm text-cyan-900 dark:text-cyan-100">Loading workbook details…</div>}
          {selectedDetail && (
            <WorkbookDetailPanel
              detail={selectedDetail}
              onClose={() => setSelectedDetail(null)}
              onOpenStudio={() => { setActiveSection('studio'); setStudioMode('enhanced'); void renderWorkbookStudio(selectedDetail, 'enhanced'); }}
              onExport={() => void exportWorkbookJson(selectedDetail)}
              onExportPdf={() => void exportWorkbookPdf(selectedDetail)}
              canOpenInteractive={canOpenInteractive}
              canExport={canExport}
              canGenerateAi={canGenerateAi}
              onAiAnalysis={() => void runWorkbookAiAnalysis(selectedDetail)}
            />
          )}

          <div className="mt-5 grid gap-4 xl:grid-cols-3">
            {displayCards.map((item) => (
              <WorkbookCard key={item.id} item={item} canPreview={canPreview} canExport={canExport} canDeploy={canDeploy} canOpenInteractive={canOpenInteractive} onPreview={() => void openWorkbookPreview(item)} onExport={() => void exportWorkbookJson(item)} onStudio={() => void openWorkbookStudio(item)} onFullscreen={() => openWorkbookFullscreen(item)} onDeploy={() => { setDeploymentWorkbookId(item.id); setDeploymentName(`ks-${item.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)); setActiveSection('deployments'); }} />
            ))}
          </div>
        </section>
      )}

      {activeSection === 'oob' && (
        <SourceSyncPanel
          icon={<DatabaseZap className="h-5 w-5" />}
          title="OOB Sentinel Workbooks"
          description="Scans the configured local Azure-Sentinel repository cache. Default discovery checks Workbooks and Solutions folders under the local repo root. No GitHub network call is made by the app."
          roots={summary?.sources?.oobRoots ?? []}
          canSync={canSyncOob}
          busy={actionBusy === 'sync-oob'}
          actionLabel="Run OOB Sync"
          onSync={() => void runAction('/workbooks/sync/oob', 'sync-oob', 'OOB workbook sync completed')}
          bullets={['Recursive JSON scanner', 'Workbook/ARM template parser', 'Required table extraction', 'MITRE technique hints', 'Sync run validation history']}
        />
      )}

      {activeSection === 'custom' && (
        <div className="space-y-5">
          <SourceSyncPanel
            icon={<UploadCloud className="h-5 w-5" />}
            title="Custom Workbooks"
            description="Scans governed custom workbook folders. The starter path supports the Defender XDR community workbook pack once it is cloned locally on the VM."
            roots={summary?.sources?.customRoots ?? []}
            canSync={canSyncCustom}
            busy={actionBusy === 'sync-custom'}
            actionLabel="Run Custom Sync"
            onSync={() => void runAction('/workbooks/sync/custom', 'sync-custom', 'Custom workbook sync completed')}
            bullets={['Defender XDR starter pack ready', 'Custom folder scanning', 'Version records by hash', 'Validation warnings', 'Source path traceability']}
          />
          <ImportWorkbookPanel
            canImport={canImport}
            filename={importFilename}
            jsonText={importText}
            result={importResult}
            busy={actionBusy === 'import-validate' || actionBusy === 'import-save'}
            onFilenameChange={setImportFilename}
            onJsonTextChange={setImportText}
            onFileSelected={(file) => void handleImportFile(file)}
            onValidate={() => void validateOrSaveImport(false)}
            onSave={() => void validateOrSaveImport(true)}
          />
          <section className="rounded-3xl border border-border bg-card p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-xl font-bold text-foreground">Custom / Imported Workbook Catalog</h3>
                <p className="mt-1 text-sm text-muted-foreground">Shows governed custom packs and imported JSON workbooks after sync/save, without needing to switch back to the global catalog.</p>
              </div>
              <button onClick={() => { setActiveSection('catalog'); setSourceFilters(['custom', 'imported']); }} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-900 dark:text-cyan-100">View in Catalog</button>
            </div>
            <div className="mt-5 grid gap-4 xl:grid-cols-3">
              {customAndImportedWorkbooks.length === 0 ? (
                <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-100 xl:col-span-3">No custom or imported workbooks are present yet. Run Custom Sync or save an imported JSON workbook above.</div>
              ) : customAndImportedWorkbooks.slice(0, 12).map((item) => (
                <WorkbookCard key={item.id} item={item} canPreview={canPreview} canExport={canExport} canDeploy={canDeploy} canOpenInteractive={canOpenInteractive} onPreview={() => void openWorkbookPreview(item)} onExport={() => void exportWorkbookJson(item)} onStudio={() => void openWorkbookStudio(item)} onFullscreen={() => openWorkbookFullscreen(item)} onDeploy={() => { setDeploymentWorkbookId(item.id); setDeploymentName(`ks-${item.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)); setActiveSection('deployments'); }} />
              ))}
            </div>
          </section>
        </div>
      )}

      {activeSection === 'studio' && (
        <InteractiveWorkbookStudio
          catalog={catalog}
          selectedDetail={selectedDetail}
          studioPayload={studioPayload}
          studioMode={studioMode}
          timeRange={timeRange}
          customerName={customers.find((c) => c.id === (studioCustomerId || customerId))?.name ?? customerName}
          customerId={studioCustomerId || customerId}
          customers={customers}
          selectedCustomerId={studioCustomerId || customerId || ''}
          onCustomerChange={setStudioCustomerId}
          canOpenInteractive={canOpenInteractive}
          busy={actionBusy === 'studio-render'}
          onSelectWorkbook={async (id) => {
            const item = catalog.find((row) => row.id === id);
            if (!item) return;
            setParameterOverrides({});
            setParameterHints({});
            const detail = await loadWorkbookDetails(item);
            if (detail && canOpenInteractive && studioMode !== 'json_template') await renderWorkbookStudio(detail, studioMode);
          }}
          onModeChange={(mode) => void changeStudioMode(mode)}
          onTimeRangeChange={setTimeRange}
          onRender={() => void renderWorkbookStudio()}
          onExport={() => selectedDetail ? void exportWorkbookJson(selectedDetail) : undefined}
          onExportPdf={() => void exportWorkbookPdf(selectedDetail ?? undefined)}
          onAiAnalysis={() => void runWorkbookAiAnalysis(selectedDetail ?? undefined)}
          parameterOverrides={parameterOverrides}
          parameterHints={parameterHints}
          showParameterEditor={showParameterEditor}
          onToggleParameterEditor={() => setShowParameterEditor((prev) => !prev)}
          onParameterChange={updateParameterOverride}
          onParameterClear={clearParameterOverride}
          onGenerateParameterHints={() => void generateParameterHints()}
          onApplyParameterHint={applyParameterHint}
          onApplyAllParameterHints={applyAllParameterHints}
          parameterHintBusy={actionBusy === 'parameter-hints'}
          canExport={canExport}
          canGenerateAi={canGenerateAi}
        />
      )}

      {activeSection === 'deployments' && (
        <DeploymentWizardPanel
          catalog={catalog}
          customers={customers}
          deployments={deployments}
          selectedWorkbookId={deploymentWorkbookId}
          selectedCustomerId={deploymentCustomerId}
          subscriptionId={deploymentSubscriptionId}
          resourceGroup={deploymentResourceGroup}
          workspaceName={deploymentWorkspaceName}
          location={deploymentLocation}
          deploymentName={deploymentName}
          enableAiAnalysis={deploymentEnableAi}
          confirmDeploy={deploymentConfirm}
          plan={deploymentPlan}
          busyKey={actionBusy}
          canDeploy={canDeploy}
          canExport={canExport}
          canRollback={canRollback}
          onWorkbookChange={(id) => { setDeploymentWorkbookId(id); const item = catalog.find((row) => row.id === id); if (item) setDeploymentName(`ks-${item.displayName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)); }}
          onCustomerChange={setDeploymentCustomerId}
          onSubscriptionChange={setDeploymentSubscriptionId}
          onResourceGroupChange={setDeploymentResourceGroup}
          onWorkspaceNameChange={setDeploymentWorkspaceName}
          onLocationChange={setDeploymentLocation}
          onDeploymentNameChange={setDeploymentName}
          onEnableAiChange={setDeploymentEnableAi}
          onConfirmChange={setDeploymentConfirm}
          onGeneratePlan={() => void generateDeploymentPlan()}
          onExportArm={() => void exportArmPayload()}
          onDeploy={() => void deployWorkbook()}
          onRollback={(id) => void rollbackDeployment(id)}
        />
      )}

      {activeSection === 'generator' && (
        <AiWorkbookGeneratorPanel
          prompt={generatorPrompt}
          mode={generatorMode}
          tables={generatorTables}
          result={generatorResult}
          customers={customers}
          selectedCustomerId={studioCustomerId || customerId || ''}
          onCustomerChange={setStudioCustomerId}
          tableSuggestions={optionValues.tables.map((option) => option.value)}
          busyKey={actionBusy}
          canGenerate={canGenerateAi}
          canExport={canExport}
          canDeploy={canDeploy}
          onPromptChange={setGeneratorPrompt}
          onModeChange={setGeneratorMode}
          onTablesChange={setGeneratorTables}
          onGenerate={() => void generateAiWorkbook(false)}
          onSave={() => void generateAiWorkbook(true)}
          onExportJson={exportGeneratedWorkbookJson}
          onOpenStudio={() => void openGeneratedWorkbookInStudio()}
          onDeploy={() => void deployGeneratedWorkbook()}
        />
      )}

      <section className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><Clock3 className="h-4 w-4" /> Recent Sync Runs</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {syncRuns.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">No sync runs have been recorded yet.</div>
          ) : syncRuns.slice(0, 6).map((run) => (
            <div key={run.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3"><span className={`rounded-full border px-2 py-1 text-[11px] ${sourceBadgeClass(run.sourceType)}`}>{sourceBadge(run.sourceType)}</span><span className="text-xs text-muted-foreground">{formatDate(run.startedAt)}</span></div>
              <div className="mt-3 font-semibold text-foreground">{run.status}</div>
              <div className="mt-2 text-xs text-muted-foreground">Files {run.filesScanned} · Inserted {run.insertedCount} · Updated {run.updatedCount} · Unchanged {run.unchangedCount ?? 0} · Skipped {run.skippedCount}</div>
              {(run.validationErrors?.length ?? 0) > 0 && <div className="mt-2 text-xs text-amber-800 dark:text-amber-200">{run.validationErrors?.length} validation warning(s)</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
      <div className="flex items-center justify-between text-cyan-900 dark:text-cyan-100">{icon}<CheckCircle2 className="h-3.5 w-3.5 text-emerald-800 dark:text-emerald-300" /></div>
      <div className="mt-3 text-2xl font-black text-foreground">{value}</div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}


function FullscreenWorkbookStudio({
  catalog,
  selectedDetail,
  studioPayload,
  activeTab,
  timeRange,
  customerName,
  customers,
  selectedCustomerId,
  busy,
  parameterOverrides,
  parameterHints,
  parameterOptions,
  canExport,
  canGenerateAi,
  canOpenInteractive,
  onTabChange,
  onClose,
  onSelectWorkbook,
  onCustomerChange,
  onTimeRangeChange,
  onRender,
  onExport,
  onExportPdf,
  onAiAnalysis,
  onParameterChange,
  onParameterClear,
  onGenerateParameterHints,
  onApplyParameterHint,
  onApplyAllParameterHints,
  onCloneToCustom,
  onSavePreset,
  onSaveView,
  onDeployFromFullscreen,
  canDeploy,
}: {
  catalog: WorkbookCatalogItem[];
  selectedDetail: WorkbookDetailResponse | null;
  studioPayload: WorkbookRenderPayload | null;
  activeTab: 'overview' | 'visuals' | 'parameters' | 'queries' | 'json';
  timeRange: string;
  customerName?: string;
  customers: CustomerOption[];
  selectedCustomerId: string;
  busy: boolean;
  parameterOverrides: Record<string, string>;
  parameterHints: Record<string, WorkbookParameterHint>;
  parameterOptions: Record<string, string[]>;
  canExport: boolean;
  canGenerateAi: boolean;
  canOpenInteractive: boolean;
  onTabChange: (tab: 'overview' | 'visuals' | 'parameters' | 'queries' | 'json') => void;
  onClose: () => void;
  onSelectWorkbook: (id: string) => Promise<void>;
  onCustomerChange: (id: string) => void;
  onTimeRangeChange: (value: string) => void;
  onRender: () => void;
  onExport: () => void;
  onExportPdf: () => void;
  onAiAnalysis: () => void;
  onParameterChange: (name: string, value: string) => void;
  onParameterClear: (name: string) => void;
  onGenerateParameterHints: () => void;
  onApplyParameterHint: (name: string) => void;
  onApplyAllParameterHints: () => void;
  onCloneToCustom: () => void;
  onSavePreset: () => void;
  onSaveView: () => void;
  onDeployFromFullscreen: () => void;
  canDeploy: boolean;
}) {
  const runtime = studioPayload?.runtime;
  const tabs: Array<{ id: 'overview' | 'visuals' | 'parameters' | 'queries' | 'json'; label: string; icon: ReactNode }> = [
    { id: 'overview', label: 'Workbook Info', icon: <BookOpenCheck className="h-4 w-4" /> },
    { id: 'visuals', label: 'Live Visuals', icon: <LayoutDashboard className="h-4 w-4" /> },
    { id: 'parameters', label: 'Parameters', icon: <Wand2 className="h-4 w-4" /> },
    { id: 'queries', label: 'KQL Blocks', icon: <Code2 className="h-4 w-4" /> },
    { id: 'json', label: 'JSON', icon: <FileJson className="h-4 w-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-[80] overflow-auto bg-slate-50 text-slate-900 dark:bg-[#07111f] dark:text-slate-100">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="flex flex-col gap-3 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground dark:text-slate-400">
              <span>HuntOps</span><span>›</span><span>Workbooks</span><span>›</span><span className="font-semibold text-slate-700 dark:text-slate-200">Fullscreen Studio</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-slate-950 dark:text-white">{selectedDetail?.workbook.displayName ?? 'Opening workbook…'}</h1>
              {selectedDetail && <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${sourceBadgeClass(selectedDetail.workbook.sourceType)}`}>{sourceBadge(selectedDetail.workbook.sourceType)}</span>}
              {runtime?.state && <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${runtimeStatusClass(runtime.state)}`}>{String(runtime.state).replace(/_/g, ' ')}</span>}
            </div>
            <div className="mt-1 text-sm text-muted-foreground dark:text-slate-400">{customerName ?? 'Select a customer/workspace'} · {selectedDetail?.workbook.category ?? 'Workbook'} · rendered {studioPayload ? formatDate(studioPayload.generatedAt) : 'not yet'}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-muted"><X className="mr-1 inline h-4 w-4" /> Close</button>
            <button onClick={onRender} disabled={!canOpenInteractive || !selectedDetail || busy} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-700 hover:bg-cyan-500/20 disabled:opacity-50 dark:text-cyan-100"><RefreshCw className={`mr-1 inline h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Refresh</button>
            <button onClick={onAiAnalysis} disabled={!canGenerateAi || !selectedDetail || busy} className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-500/20 disabled:opacity-50 dark:text-fuchsia-100"><Sparkles className="mr-1 inline h-4 w-4" /> AI Analysis</button>
            <button onClick={onExport} disabled={!canExport || !selectedDetail} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"><Download className="mr-1 inline h-4 w-4" /> Export JSON</button>
            <button onClick={onExportPdf} disabled={!canExport || !selectedDetail} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-100"><Download className="mr-1 inline h-4 w-4" /> Export PDF</button>
            <button onClick={onCloneToCustom} disabled={!selectedDetail} className="rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-500/20 disabled:opacity-50 dark:text-violet-100"><Layers3 className="mr-1 inline h-4 w-4" /> Clone Custom</button>
            <button onClick={onSavePreset} disabled={!selectedDetail} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"><CheckCircle2 className="mr-1 inline h-4 w-4" /> Save Preset</button>
            <button onClick={onSaveView} disabled={!selectedDetail} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-700 hover:bg-cyan-500/20 disabled:opacity-50 dark:text-cyan-100"><LayoutDashboard className="mr-1 inline h-4 w-4" /> Save View</button>
            <button onClick={onDeployFromFullscreen} disabled={!canDeploy || !selectedDetail} className="rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-500/20 disabled:opacity-50 dark:text-orange-100"><Rocket className="mr-1 inline h-4 w-4" /> Deploy</button>
          </div>
        </div>

        <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <div className="grid gap-3 xl:grid-cols-[220px_320px_160px_minmax(0,1fr)]">
            <select value={selectedCustomerId} onChange={(e) => onCustomerChange(e.target.value)} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
              <option value="">Select customer</option>
              {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
            </select>
            <select value={selectedDetail?.workbook.id ?? ''} onChange={(e) => void onSelectWorkbook(e.target.value)} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
              <option value="">Select workbook</option>
              {catalog.slice(0, 700).map((row) => <option key={row.id} value={row.id}>{row.displayName}</option>)}
            </select>
            <select value={timeRange} onChange={(e) => onTimeRangeChange(e.target.value)} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
              <option value="1h">Last 1 hour</option>
              <option value="4h">Last 4 hours</option>
              <option value="8h">Last 8 hours</option>
              <option value="12h">Last 12 hours</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <FullscreenParameterRibbon
              parameters={studioPayload?.parameters ?? []}
              overrides={parameterOverrides}
              hints={parameterHints}
              dynamicOptions={parameterOptions}
              canGenerateAi={canGenerateAi}
              onChange={onParameterChange}
              onClear={onParameterClear}
              onGenerateHints={onGenerateParameterHints}
              onApplyHint={onApplyParameterHint}
              onApplyAllHints={onApplyAllParameterHints}
            />
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto border-t border-slate-200 px-5 py-2 dark:border-slate-800">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => onTabChange(tab.id)} className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition ${activeTab === tab.id ? 'bg-cyan-600 text-white shadow-sm dark:bg-cyan-500 dark:text-slate-950' : 'text-muted-foreground hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-card'}`}>{tab.icon}{tab.label}</button>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-[1800px] space-y-5 p-5">
        {!selectedDetail && <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">Loading workbook details or waiting for a workbook selection…</div>}

        {selectedDetail && activeTab === 'overview' && (
          <div className="grid gap-5 xl:grid-cols-[0.85fr_1.15fr]">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
              <h2 className="text-xl font-bold text-slate-950 dark:text-white">{selectedDetail.workbook.displayName}</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground dark:text-slate-400">{selectedDetail.workbook.description || 'No description detected in workbook JSON.'}</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <DetailMetric label="Required tables" value={selectedDetail.workbook.requiredTables.length} />
                <DetailMetric label="Query blocks" value={selectedDetail.queryBlocks.length} />
                <DetailMetric label="Parameters" value={studioPayload?.parameters?.length ?? selectedDetail.jsonMetadata.parameterNames?.length ?? 0} />
                <DetailMetric label="Visual items" value={selectedDetail.jsonMetadata.visualLikeItemCount ?? 0} />
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {selectedDetail.workbook.requiredTables.slice(0, 16).map((table) => <span key={table} className="rounded-md border border-cyan-200 bg-cyan-50 px-2 py-1 font-mono text-xs text-cyan-800 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100">{table}</span>)}
              </div>
            </section>
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
              <LiveRuntimeOverview payload={studioPayload} />
            </section>
          </div>
        )}

        {selectedDetail && activeTab === 'visuals' && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-4 flex items-center justify-between gap-3"><h2 className="text-lg font-bold text-slate-950 dark:text-white">Live Rendered Workbook Visuals</h2><button onClick={onRender} disabled={busy} className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-700 dark:text-cyan-100">Refresh visuals</button></div>
            <RuntimeResultsPanel payload={studioPayload} />
          </section>
        )}

        {selectedDetail && activeTab === 'parameters' && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <ParameterResolverPanel
              parameters={studioPayload?.parameters ?? []}
              runtime={studioPayload?.runtime}
              overrides={parameterOverrides}
              hints={parameterHints}
              dynamicOptions={parameterOptions}
              showEditor
              hintBusy={false}
              canGenerateAi={canGenerateAi}
              onToggleEditor={() => undefined}
              onChange={onParameterChange}
              onClear={onParameterClear}
              onGenerateHints={onGenerateParameterHints}
              onApplyHint={onApplyParameterHint}
              onApplyAllHints={onApplyAllParameterHints}
            />
          </section>
        )}

        {selectedDetail && activeTab === 'queries' && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <h2 className="text-lg font-bold text-slate-950 dark:text-white">Extracted KQL Blocks</h2>
            <div className="mt-4 grid gap-3 xl:grid-cols-2">
              {selectedDetail.queryBlocks.length === 0 ? <div className="text-sm text-muted-foreground dark:text-slate-400">No KQL query blocks were confidently extracted from this workbook.</div> : selectedDetail.queryBlocks.map((query) => (
                <div key={query.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="flex items-center justify-between gap-3"><div className="font-semibold text-slate-950 dark:text-white">{query.title}</div><div className="text-xs text-muted-foreground">{query.characterCount} chars</div></div>
                  <div className="mt-2 flex flex-wrap gap-2">{query.requiredTables.slice(0, 8).map((table) => <span key={table} className="rounded-md border border-slate-300 px-2 py-1 font-mono text-[11px] text-muted-foreground dark:border-slate-700 dark:text-slate-300">{table}</span>)}</div>
                  <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-[11px] leading-5 text-slate-700 dark:border-slate-800 dark:bg-black/25 dark:text-slate-300">{query.query}</pre>
                </div>
              ))}
            </div>
          </section>
        )}

        {selectedDetail && activeTab === 'json' && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-950 dark:text-white">Workbook JSON</h2><button onClick={onExport} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">Export JSON</button></div>
            <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11px] leading-5 text-slate-700 dark:border-slate-800 dark:bg-black/25 dark:text-slate-300">{selectedDetail.workbookJsonPreview}</pre>
          </section>
        )}
      </main>
    </div>
  );
}


function RuntimeResultsPanel({ payload }: { payload: WorkbookRenderPayload | null }) {
  const results = payload?.queryResults ?? [];
  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
        No live query results are available yet. Select a customer/workspace context and click Refresh. Parameter-heavy workbook blocks may need native parameter values before execution.
      </div>
    );
  }
  return <div className="grid gap-4">{results.map((result) => <QueryResultCard key={result.id} result={result} />)}</div>;
}

function FullscreenParameterRibbon({
  parameters,
  overrides,
  hints,
  dynamicOptions,
  canGenerateAi,
  onChange,
  onClear,
  onGenerateHints,
  onApplyHint,
  onApplyAllHints,
}: {
  parameters: WorkbookRuntimeParameter[];
  overrides: Record<string, string>;
  hints: Record<string, WorkbookParameterHint>;
  dynamicOptions?: Record<string, string[]>;
  canGenerateAi: boolean;
  onChange: (name: string, value: string) => void;
  onClear: (name: string) => void;
  onGenerateHints: () => void;
  onApplyHint: (name: string) => void;
  onApplyAllHints: () => void;
}) {
  const visible = parameters.slice(0, 8);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {visible.length === 0 && <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-muted-foreground dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">No workbook parameters detected yet. Click Refresh after selecting a workbook.</span>}
      {visible.map((parameter) => <NativeWorkbookParameterControl key={parameter.name} parameter={parameter} value={overrides[parameter.name] ?? String(parameter.selectedValue ?? '')} hint={hints[parameter.name]} dynamicOptions={dynamicOptions?.[parameter.name] ?? []} onChange={onChange} onClear={onClear} onApplyHint={onApplyHint} />)}
      <button onClick={onGenerateHints} disabled={!canGenerateAi || parameters.length === 0} className="h-9 rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 text-xs font-semibold text-fuchsia-700 disabled:opacity-50 dark:text-fuchsia-100">AI hints</button>
      <button onClick={onApplyAllHints} disabled={Object.keys(hints).length === 0} className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">Apply hints</button>
    </div>
  );
}

function NativeWorkbookParameterControl({ parameter, value, hint, dynamicOptions, onChange, onClear, onApplyHint }: { parameter: WorkbookRuntimeParameter; value: string; hint?: WorkbookParameterHint; dynamicOptions?: string[]; onChange: (name: string, value: string) => void; onClear: (name: string) => void; onApplyHint: (name: string) => void }) {
  const normalizedType = String(parameter.type || '').toLowerCase();
  const options = uniqueStringValues([...(dynamicOptions ?? []), ...(parameter.options ?? []), ...(hint?.examples ?? []), hint?.recommendedValue ?? ''].filter(Boolean).map(String), 100);
  const inputId = `ks-wb-param-${parameter.name.replace(/[^a-z0-9_-]/gi, '-')}`;
  const isBool = /bool|toggle|checkbox/.test(normalizedType) || ['true', 'false'].includes(String(parameter.selectedValue).toLowerCase());
  const isTime = /timerange|time range|duration/.test(normalizedType) || /time.*range|range.*time/i.test(parameter.name);

  return (
    <label className="group relative min-w-[170px] max-w-[260px]">
      <span className="mb-1 block truncate text-[10px] font-bold uppercase tracking-wide text-muted-foreground dark:text-slate-400" title={parameter.label}>{parameter.label}</span>
      {isBool ? (
        <select value={value || String(parameter.selectedValue ?? 'false')} onChange={(e) => onChange(parameter.name, e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      ) : isTime ? (
        <select value={value || String(parameter.selectedValue ?? '24h')} onChange={(e) => onChange(parameter.name, e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
          {['1h','4h','8h','12h','24h','7d','30d','60d','90d'].map((range) => <option key={range} value={range}>{range}</option>)}
        </select>
      ) : options.length > 0 ? (
        <select value={value || String(parameter.selectedValue ?? '')} onChange={(e) => onChange(parameter.name, e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]">
          <option value="">Select / All</option>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input list={`${inputId}-list`} value={value || String(parameter.selectedValue ?? '')} onChange={(e) => onChange(parameter.name, e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-900 outline-none focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:[color-scheme:dark]" placeholder="All / value" />
      )}
      {options.length > 0 && <datalist id={`${inputId}-list`}>{options.map((option) => <option key={option} value={option} />)}</datalist>}
      <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-80 rounded-xl border border-slate-200 bg-white p-3 text-xs text-muted-foreground shadow-xl group-hover:block dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        <div><strong>{parameter.name}</strong> · {parameter.source} · {parameter.defaulted ? 'defaulted' : 'overridden'}</div>
        {hint && <div className="mt-2 text-fuchsia-700 dark:text-fuchsia-200">AI hint: {hint.recommendedValue} - {hint.reason}</div>}
        <div className="mt-2 flex gap-2">
          {hint && <button type="button" onClick={(e) => { e.preventDefault(); onApplyHint(parameter.name); }} className="rounded-md border border-fuchsia-400/30 px-2 py-1">Use hint</button>}
          <button type="button" onClick={(e) => { e.preventDefault(); onClear(parameter.name); }} className="rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700">Default</button>
        </div>
      </div>
    </label>
  );
}

function WorkbookCard({
  item,
  canPreview,
  canExport,
  canDeploy,
  canOpenInteractive,
  onPreview,
  onExport,
  onStudio,
  onFullscreen,
  onDeploy,
}: {
  item: WorkbookCatalogItem;
  canPreview: boolean;
  canExport: boolean;
  canDeploy: boolean;
  canOpenInteractive: boolean;
  onPreview: () => void;
  onExport: () => void;
  onStudio: () => void;
  onFullscreen: () => void;
  onDeploy: () => void;
}) {
  return (
    <article className="rounded-3xl border border-border bg-card p-5 shadow-xl shadow-slate-950/30 transition hover:-translate-y-1 hover:border-cyan-400/40">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${sourceBadgeClass(item.sourceType)}`}>{sourceBadge(item.sourceType)}</span>
        <div className="flex flex-wrap justify-end gap-2">
          <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${runnableClass(item.runnableStatus)}`}>{runnableLabel(item.runnableStatus)}</span>
          <span className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-wide ${validationClass(item.validationStatus)}`}>{item.validationStatus ?? 'unknown'}</span>
        </div>
      </div>
      <h4 className="mt-4 text-lg font-bold text-foreground">{item.displayName}</h4>
      <div className="mt-2 flex flex-wrap gap-2">
        <span className="rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">{item.category}</span>
        {item.solutionName && <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[11px] text-cyan-900 dark:text-cyan-100">{item.solutionName}</span>}
        {!item.solutionName && item.packName && <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[11px] text-cyan-900 dark:text-cyan-100">{item.packName}</span>}
        {typeof item.queryCount === 'number' && <span className="rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">{item.queryCount} query hints</span>}
      </div>
      <p className="mt-3 min-h-[72px] text-sm leading-6 text-muted-foreground">{item.description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {item.requiredTables.length === 0 ? <span className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-100">No tables detected</span> : item.requiredTables.slice(0, 7).map((table) => <span key={table} className={`rounded-lg border px-2 py-1 font-mono text-[11px] ${tableChipClass(table, item)}`}>{table}</span>)}
      </div>
      {(item.missingTables?.length ?? 0) > 0 && <div className="mt-2 text-[11px] font-semibold text-orange-800 dark:text-orange-200">Missing: {item.missingTables?.join(', ')}.</div>}
      {item.runnableStatus === 'unknown_data_readiness' && <div className="mt-2 text-[11px] font-semibold text-amber-800 dark:text-amber-200">Workspace table readiness has not been checked for this customer.</div>}
      <div className="mt-4 flex flex-wrap gap-2">
        {item.requiredConnectors.slice(0, 3).map((connector) => <span key={connector} className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-900 dark:text-cyan-100">{connector}</span>)}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {item.tags.slice(0, 5).map((tag) => <span key={tag} className="text-[11px] text-cyan-900 dark:text-cyan-200/80">#{tag}</span>)}
        {(item.warningBuckets ?? []).slice(0, 2).map((bucket) => <span key={bucket} className="text-[11px] text-amber-800 dark:text-amber-200/80">#{bucket}</span>)}
      </div>
      <div className="mt-4 truncate text-[11px] text-muted-foreground" title={item.sourceRelativePath || item.sourcePath}>{item.sourceRelativePath || item.sourcePath || '-'}</div>
      <div className="mt-5 grid grid-cols-2 gap-2 text-xs xl:grid-cols-5">
        <button onClick={onPreview} disabled={!canPreview} className="rounded-xl border border-border bg-card px-3 py-2 text-muted-foreground hover:border-cyan-400/40 disabled:cursor-not-allowed disabled:opacity-45"><Eye className="mx-auto mb-1 h-4 w-4" />Preview</button>
        <button onClick={onExport} disabled={!canExport} className="rounded-xl border border-border bg-card px-3 py-2 text-muted-foreground hover:border-cyan-400/40 disabled:cursor-not-allowed disabled:opacity-45"><Download className="mx-auto mb-1 h-4 w-4" />Export</button>
        <button onClick={onStudio} disabled={!canOpenInteractive} className="rounded-xl border border-border bg-card px-3 py-2 text-muted-foreground hover:border-cyan-400/40 disabled:cursor-not-allowed disabled:opacity-45"><LayoutDashboard className="mx-auto mb-1 h-4 w-4" />Studio</button>
        <button onClick={onFullscreen} disabled={!canOpenInteractive || item.id.startsWith('roadmap-')} className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-45"><LayoutDashboard className="mx-auto mb-1 h-4 w-4" />Fullscreen</button>
        <button onClick={onDeploy} disabled={!canDeploy || item.id.startsWith('roadmap-')} className="rounded-xl border border-border bg-card px-3 py-2 text-muted-foreground hover:border-cyan-400/40 disabled:cursor-not-allowed disabled:opacity-45"><Rocket className="mx-auto mb-1 h-4 w-4" />Deploy</button>
      </div>
    </article>
  );

}

function WorkbookDetailPanel({
  detail,
  onClose,
  onOpenStudio,
  onExport,
  onExportPdf,
  canOpenInteractive,
  canExport,
  canGenerateAi,
  onAiAnalysis,
}: {
  detail: WorkbookDetailResponse;
  onClose: () => void;
  onOpenStudio: () => void;
  onExport: () => void;
  onExportPdf: () => void;
  canOpenInteractive: boolean;
  canExport: boolean;
  canGenerateAi: boolean;
  onAiAnalysis: () => void;
}) {
  const workbook = detail.workbook;
  return (
    <section className="mt-6 rounded-3xl border border-cyan-400/20 bg-card p-5 shadow-xl shadow-cyan-950/20">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${sourceBadgeClass(workbook.sourceType)}`}>{sourceBadge(workbook.sourceType)}</span>
            <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${runnableClass(workbook.runnableStatus)}`}>{runnableLabel(workbook.runnableStatus)}</span>
            <span className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-wide ${validationClass(workbook.validationStatus)}`}>{workbook.validationStatus ?? 'unknown'}</span>
          </div>
          <h3 className="mt-3 text-2xl font-black text-foreground">{workbook.displayName}</h3>
          <p className="mt-2 max-w-5xl text-sm leading-6 text-muted-foreground">{workbook.description || 'No workbook description was found in the source JSON.'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onExport} disabled={!canExport} className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:border-cyan-400/40 disabled:opacity-50"><Download className="h-4 w-4" /> Export JSON</button>
          <button onClick={onExportPdf} disabled={!canExport} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"><Download className="h-4 w-4" /> Export PDF</button>
          <button onClick={onOpenStudio} disabled={!canOpenInteractive} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-50"><LayoutDashboard className="h-4 w-4" /> Open Studio</button>
          <button onClick={onAiAnalysis} disabled={!canGenerateAi} className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-2 text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100 hover:bg-fuchsia-500/20 disabled:opacity-50"><Sparkles className="h-4 w-4" /> AI Analysis</button>
          <button onClick={onClose} className="rounded-xl border border-border bg-card p-2 text-muted-foreground hover:border-red-400/40"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <DetailMetric label="Required tables" value={workbook.requiredTables.length} />
        <DetailMetric label="Query blocks" value={detail.queryBlocks.length} />
        <DetailMetric label="Connectors" value={workbook.requiredConnectors.length} />
        <DetailMetric label="JSON resources" value={detail.jsonMetadata.resourceTypes?.length ?? 0} />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><DatabaseZap className="h-4 w-4" /> Required Tables</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {workbook.requiredTables.length === 0 ? <span className="rounded-lg border border-amber-400/20 bg-amber-500/10 px-2 py-1 text-xs text-amber-800 dark:text-amber-100">No tables detected</span> : workbook.requiredTables.map((table) => <span key={table} className={`rounded-lg border px-2 py-1 font-mono text-xs ${tableChipClass(table, workbook)}`}>{table}</span>)}
          </div>
          {(workbook.missingTables?.length ?? 0) > 0 && <div className="mt-3 text-xs font-semibold text-orange-800 dark:text-orange-200">Missing tables: {workbook.missingTables?.join(', ')}</div>}
          <div className="mt-4 text-xs text-muted-foreground">Source: {workbook.sourceRelativePath || workbook.sourcePath || '-'}</div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><FileSearch className="h-4 w-4" /> JSON Metadata</div>
          <div className="mt-3 grid gap-2 text-sm text-muted-foreground">
            <div>Version: <span className="text-muted-foreground">{detail.version.label || 'current'}</span></div>
            <div>Serialized data: <span className="text-muted-foreground">{detail.jsonMetadata.serializedDataPresent ? 'yes' : 'no / not detected'}</span></div>
            <div>Workbook resources: <span className="text-muted-foreground">{detail.jsonMetadata.workbookResourceCount ?? 0}</span></div>
            <div>Visual-like items: <span className="text-muted-foreground">{detail.jsonMetadata.visualLikeItemCount ?? 0}</span></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(detail.jsonMetadata.parameterNames ?? []).slice(0, 12).map((name) => <span key={name} className="rounded-lg border border-border bg-card px-2 py-1 font-mono text-[11px] text-muted-foreground">{name}</span>)}
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><Code2 className="h-4 w-4" /> Extracted Query Blocks</div>
        <div className="mt-3 grid gap-3 xl:grid-cols-2">
          {detail.queryBlocks.length === 0 ? <div className="text-sm text-muted-foreground">No KQL query blocks were confidently extracted from this workbook JSON.</div> : detail.queryBlocks.slice(0, 6).map((query) => (
            <div key={query.id} className="rounded-2xl border border-border bg-card p-3">
              <div className="flex items-center justify-between gap-3"><div className="font-semibold text-foreground">{query.title}</div><div className="text-xs text-muted-foreground">{query.characterCount} chars</div></div>
              <div className="mt-2 flex flex-wrap gap-2">{query.requiredTables.slice(0, 5).map((table) => <span key={table} className="rounded-md border border-border px-2 py-1 font-mono text-[11px] text-muted-foreground">{table}</span>)}</div>
              <pre className="mt-3 max-h-40 overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-black/30 p-3 text-[11px] leading-5 text-muted-foreground">{query.preview}</pre>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DetailMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-2xl font-black text-foreground">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}


function runtimeStatusClass(status?: string): string {
  if (status === 'rendered' || status === 'completed') return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100';
  if (status === 'no_data') return 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
  if (status === 'skipped' || status === 'missing_tables' || status === 'completed_with_warnings') return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100';
  if (status === 'failed' || status === 'forbidden') return 'border-red-300 bg-red-50 text-red-800 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-100';
  return 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-500/10 dark:text-cyan-100';
}

function formatRuntimeValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) return formatDate(raw);
  return raw.length > 140 ? `${raw.slice(0, 140)}…` : raw;
}

function exportRuntimeCsv(workbookName: string, results: WorkbookRuntimeQueryResult[]): void {
  const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const lines: string[] = [];
  for (const result of results) {
    lines.push([`Workbook: ${workbookName}`, `Query: ${result.title}`, `Status: ${result.status}`].map(escapeCsv).join(','));
    if (result.columns.length > 0) lines.push(result.columns.map(escapeCsv).join(','));
    for (const row of result.rows) lines.push(row.map(escapeCsv).join(','));
    lines.push('');
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kloudsecure-workbook-render-${workbookName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'results'}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function parameterDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parameterConfidenceClass(confidence?: string): string {
  if (confidence === 'high') return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100';
  if (confidence === 'medium') return 'border-amber-400/30 bg-amber-500/10 text-amber-800 dark:text-amber-100';
  return 'border-border bg-card text-muted-foreground';
}

function ParameterResolverPanel({
  parameters,
  runtime,
  overrides,
  hints,
  dynamicOptions,
  showEditor,
  hintBusy,
  canGenerateAi,
  onToggleEditor,
  onChange,
  onClear,
  onGenerateHints,
  onApplyHint,
  onApplyAllHints,
}: {
  parameters: WorkbookRuntimeParameter[];
  runtime?: WorkbookRenderPayload['runtime'];
  overrides: Record<string, string>;
  hints: Record<string, WorkbookParameterHint>;
  dynamicOptions?: Record<string, string[]>;
  showEditor: boolean;
  hintBusy: boolean;
  canGenerateAi: boolean;
  onToggleEditor: () => void;
  onChange: (name: string, value: string) => void;
  onClear: (name: string) => void;
  onGenerateHints: () => void;
  onApplyHint: (name: string) => void;
  onApplyAllHints: () => void;
}) {
  if (!parameters || parameters.length === 0) return null;
  const hintCount = Object.keys(hints).length;
  return (
    <div className="rounded-3xl border border-violet-400/20 bg-[radial-gradient(circle_at_top_left,rgba(168,85,247,0.22),transparent_34%),linear-gradient(135deg,rgba(15,23,42,0.98),rgba(2,6,23,0.98))] p-5 shadow-2xl shadow-violet-950/20">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-violet-900 dark:text-violet-100"><Wand2 className="h-4 w-4" /> Workbook Parameter Console</div>
          <h4 className="mt-3 text-xl font-black text-foreground">Edit OOB workbook parameters before live execution</h4>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-muted-foreground">Auto-filled values can be overridden below. Use AI hints to get safe starter values for customer, workspace, identity, resource group, pricing and filter parameters.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full border border-border bg-card px-3 py-1 text-muted-foreground">parameters {runtime?.parameterCount ?? parameters.length}</span>
          <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-amber-800 dark:text-amber-100">defaulted {runtime?.defaultedParameterCount ?? parameters.filter((p) => p.defaulted).length}</span>
          <span className="rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-red-800 dark:text-red-100">unresolved {runtime?.unresolvedParameterCount ?? 0}</span>
          <span className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-fuchsia-900 dark:text-fuchsia-100">hints {hintCount}</span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={onToggleEditor} className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-cyan-400/40">{showEditor ? 'Hide editor' : 'Show editor'}</button>
        <button onClick={onGenerateHints} disabled={!canGenerateAi || hintBusy} className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-2 text-xs font-semibold text-fuchsia-900 dark:text-fuchsia-100 hover:bg-fuchsia-500/20 disabled:opacity-50"><Sparkles className={`h-4 w-4 ${hintBusy ? 'animate-pulse' : ''}`} /> Generate AI hints</button>
        <button onClick={onApplyAllHints} disabled={!hintCount} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50">Apply all hints</button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {parameters.slice(0, 18).map((param) => {
          const effectiveValue = overrides[param.name] ?? parameterDisplayValue(param.selectedValue);
          return (
            <span key={param.name} className={`rounded-xl border px-3 py-2 text-xs ${overrides[param.name] !== undefined ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-900 dark:text-cyan-100' : param.defaulted ? 'border-amber-400/30 bg-amber-500/10 text-amber-800 dark:text-amber-100' : 'border-emerald-400/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100'}`} title={`${param.name} = ${effectiveValue}`}>
              <span className="font-mono">{param.name}</span><span className="mx-1 text-muted-foreground">=</span>{formatRuntimeValue(effectiveValue)}
            </span>
          );
        })}
      </div>

      {showEditor && (
        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {parameters.slice(0, 32).map((param) => {
            const currentValue = overrides[param.name] ?? parameterDisplayValue(param.selectedValue);
            const hint = hints[param.name];
            const optionValues = Array.from(new Set([...(param.options ?? []), ...(hint?.examples ?? []), 'All'].filter(Boolean))).slice(0, 16);
            return (
              <div key={param.name} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-mono text-sm font-bold text-foreground">{param.name}</div>
                      <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{param.type}</span>
                      <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">{param.source}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{param.label}</div>
                  </div>
                  {hint && <span className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${parameterConfidenceClass(hint.confidence)}`}>{hint.confidence} confidence</span>}
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
                  {optionValues.length > 1 ? (
                    <select value={currentValue} onChange={(e) => onChange(param.name, e.target.value)} className="h-10 rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300">
                      {optionValues.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : (
                    <>
                      <input list={`ks-param-options-${param.name}`} value={currentValue} onChange={(e) => onChange(param.name, e.target.value)} placeholder="Enter parameter value, e.g. All" className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none placeholder:text-muted-foreground focus:border-cyan-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-muted-foreground dark:focus:border-cyan-300" />
                      <datalist id={`ks-param-options-${param.name}`}>{uniqueStringValues([...(dynamicOptions?.[param.name] ?? []), ...(param.options ?? []), ...(hint?.examples ?? [])].map(String), 100).map((option) => <option key={option} value={option} />)}</datalist>
                    </>
                  )}
                  <button onClick={() => onClear(param.name)} className="rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-slate-500">Default</button>
                  <button onClick={() => onApplyHint(param.name)} disabled={!hint} className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-2 text-xs font-semibold text-fuchsia-900 dark:text-fuchsia-100 hover:bg-fuchsia-500/20 disabled:opacity-40">Use hint</button>
                </div>

                {hint && (
                  <div className="mt-3 rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/10 p-3 text-xs leading-5 text-fuchsia-50">
                    <div className="font-semibold text-fuchsia-900 dark:text-fuchsia-100">Suggested: <span className="font-mono">{hint.recommendedValue}</span></div>
                    <div className="mt-1 text-fuchsia-900 dark:text-fuchsia-100/80">{hint.reason}</div>
                    <div className="mt-1 text-fuchsia-900 dark:text-fuchsia-100/60">Source: {hint.source}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LiveRuntimeOverview({ payload }: { payload: WorkbookRenderPayload | null }) {
  const runtime = payload?.runtime;
  if (!runtime) return null;
  const attempted = Math.max(Number(runtime.attempted ?? 0), 1);
  const renderedPct = Math.round(((Number(runtime.rendered ?? 0) + Number(runtime.noData ?? 0)) / attempted) * 100);
  const failedPct = Math.round((Number(runtime.failed ?? 0) / attempted) * 100);
  const skippedPct = Math.round((Number(runtime.skipped ?? 0) / attempted) * 100);
  const statusText = String(runtime.state ?? 'metadata_only').replace(/_/g, ' ');
  return (
    <div className="overflow-hidden rounded-3xl border border-cyan-400/20 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.20),transparent_35%),linear-gradient(135deg,rgba(15,23,42,0.98),rgba(2,6,23,0.98))] shadow-2xl shadow-cyan-950/20">
      <div className="grid gap-0 xl:grid-cols-[1fr_1.2fr]">
        <div className="p-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-cyan-900 dark:text-cyan-100">Live KQL Runtime</div>
          <h4 className="mt-3 text-2xl font-black text-foreground">Runtime command center</h4>
          <div className="mt-2 text-sm leading-6 text-muted-foreground">{runtime.liveExecution ? 'Executed against the selected customer Log Analytics workspace with safe query guardrails and parameter injection.' : runtime.warning || 'Customer context required for live execution.'}</div>
          {runtime.workspaceId && <div className="mt-2 truncate rounded-xl border border-border bg-black/20 px-3 py-2 font-mono text-xs text-muted-foreground">Workspace: {runtime.workspaceId}</div>}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase ${runtimeStatusClass(runtime.state)}`}>{statusText}</span>
            <span className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">{runtime.timeRange ?? '24h'}</span>
            <span className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">{runtime.durationMs ?? 0} ms</span>
          </div>
        </div>
        <div className="border-t border-border/80 p-5 xl:border-l xl:border-t-0">
          <div className="grid gap-3 sm:grid-cols-3">
            <DetailMetric label="Rendered" value={runtime.rendered ?? 0} />
            <DetailMetric label="No Data" value={runtime.noData ?? 0} />
            <DetailMetric label="Skipped" value={runtime.skipped ?? 0} />
            <DetailMetric label="Failed" value={runtime.failed ?? 0} />
            <DetailMetric label="Attempted" value={runtime.attempted ?? 0} />
            <DetailMetric label="Runtime ms" value={runtime.durationMs ?? 0} />
          </div>
          <div className="mt-4 rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.16em] text-muted-foreground"><span>Execution mix</span><span>{renderedPct}% successful/no-data</span></div>
            <div className="flex h-3 overflow-hidden rounded-full bg-muted">
              <div className="bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width: `${Math.max(0, Math.min(100, renderedPct))}%` }} />
              <div className="bg-amber-400/80" style={{ width: `${Math.max(0, Math.min(100, skippedPct))}%` }} />
              <div className="bg-red-500/80" style={{ width: `${Math.max(0, Math.min(100, failedPct))}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground"><span>green rendered</span><span>amber skipped</span><span>red failed</span></div>
          </div>
        </div>
      </div>
      {runtime.warning && <div className="border-t border-amber-400/20 bg-amber-500/10 px-5 py-3 text-xs text-amber-800 dark:text-amber-100">{runtime.warning}</div>}
    </div>
  );
}

function MiniBarChart({ result }: { result: WorkbookRuntimeQueryResult }) {
  const points = result.chart?.points ?? [];
  if (points.length === 0) return null;
  const max = Math.max(...points.map((p) => Math.abs(p.value)), 1);
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground dark:text-slate-400">{result.chart?.categoryColumn ?? 'Category'} → {result.chart?.valueColumn ?? 'Value'}</div>
      {points.slice(0, 10).map((point, index) => {
        const width = Math.max(4, Math.min(100, Math.round((Math.abs(point.value) / max) * 100)));
        return (
          <div key={`${point.label}-${index}`} className="grid grid-cols-[minmax(120px,0.9fr)_2fr_72px] items-center gap-3 text-xs">
            <div className="truncate text-slate-700 dark:text-slate-300" title={point.label}>{point.label}</div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full rounded-full bg-gradient-to-r from-cyan-500/70 via-sky-400/80 to-emerald-400/80" style={{ width: `${width}%` }} />
            </div>
            <div className="text-right font-mono text-slate-800 dark:text-slate-200">{formatRuntimeValue(point.value)}</div>
          </div>
        );
      })}
    </div>
  );
}

function KpiResultGrid({ result }: { result: WorkbookRuntimeQueryResult }) {
  if (!result.rowObjects[0]) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {Object.entries(result.rowObjects[0]).slice(0, 6).map(([key, value]) => (
        <div key={key} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
          <div className="truncate text-xs uppercase tracking-wide text-muted-foreground dark:text-slate-500">{key}</div>
          <div className="mt-2 truncate text-2xl font-black text-slate-950 dark:text-white" title={String(value ?? '')}>{formatRuntimeValue(value)}</div>
        </div>
      ))}
    </div>
  );
}

function ResultTable({ result }: { result: WorkbookRuntimeQueryResult }) {
  if (result.columns.length === 0) return null;
  return (
    <div className="max-h-80 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-black/20">
      <table className="min-w-full divide-y divide-slate-200 text-left text-xs dark:divide-slate-800">
        <thead className="sticky top-0 bg-slate-100 text-muted-foreground dark:bg-slate-950/95 dark:text-slate-400">
          <tr>{result.columns.slice(0, 8).map((column) => <th key={column} className="px-3 py-2 font-semibold">{column}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
          {result.rows.slice(0, 20).map((row, rowIndex) => (
            <tr key={rowIndex} className="hover:bg-cyan-50/70 dark:hover:bg-card">
              {result.columns.slice(0, 8).map((column, columnIndex) => <td key={`${rowIndex}-${column}`} className="max-w-[260px] truncate px-3 py-2 text-slate-700 dark:text-slate-300" title={String(row[columnIndex] ?? '')}>{formatRuntimeValue(row[columnIndex])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueryResultCard({ result }: { result: WorkbookRuntimeQueryResult }) {
  const hasData = result.status === 'rendered' || result.status === 'no_data';
  const partialMissing = result.toleratedMissingTables ?? [];
  const sourceTables = result.sourceTables ?? [];
  const accent = result.status === 'rendered'
    ? 'from-emerald-50 via-cyan-50 to-white dark:from-emerald-500/15 dark:via-cyan-500/10 dark:to-slate-950'
    : result.status === 'failed'
      ? 'from-rose-50 via-white to-white dark:from-red-500/15 dark:via-slate-950 dark:to-slate-950'
      : result.status === 'skipped' || result.status === 'missing_tables'
        ? 'from-amber-50 via-white to-white dark:from-amber-500/15 dark:via-slate-950 dark:to-slate-950'
        : 'from-slate-50 via-white to-white dark:from-slate-700/20 dark:via-slate-950 dark:to-slate-950';
  return (
    <div className={`ks-workbook-runtime-card rounded-3xl border border-slate-200 bg-gradient-to-br ${accent} p-4 shadow-lg shadow-slate-200/70 dark:border-slate-800 dark:shadow-black/25`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h5 className="text-lg font-black text-slate-950 dark:text-white">{result.title}</h5>
            <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${runtimeStatusClass(result.status)}`}>{result.status.replace(/_/g, ' ')}</span>
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] uppercase tracking-wide text-muted-foreground shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">{result.visualization}</span>
            {result.partialExecution && <span className="rounded-full border border-cyan-300 bg-cyan-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-500/10 dark:text-cyan-100">partial-safe</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {result.requiredTables.slice(0, 10).map((table) => {
              const missing = result.missingTables.includes(table);
              const tolerated = partialMissing.includes(table);
              const tone = tolerated
                ? 'border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-500/10 dark:text-cyan-100'
                : missing
                  ? 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-400/40 dark:bg-orange-500/10 dark:text-orange-100'
                  : 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-100';
              return <span key={table} className={`rounded-md border px-2 py-1 font-mono text-[11px] ${tone}`}>{table}</span>;
            })}
          </div>
          {sourceTables.length > 0 && (
            <div className="mt-2 text-[11px] text-muted-foreground dark:text-slate-400">
              Source table(s): <span className="font-mono">{sourceTables.slice(0, 8).join(', ')}</span>{partialMissing.length > 0 ? ` · ignored missing union member(s): ${partialMissing.join(', ')}` : ''}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs text-muted-foreground dark:text-slate-400">
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/80"><div className="font-mono text-lg font-black text-slate-950 dark:text-white">{result.rowCount}</div><div className="uppercase tracking-wide">rows</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/80"><div className="font-mono text-lg font-black text-slate-950 dark:text-white">{result.columns.length}</div><div className="uppercase tracking-wide">cols</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950/80"><div className="font-mono text-lg font-black text-slate-950 dark:text-white">{result.durationMs}</div><div className="uppercase tracking-wide">ms</div></div>
        </div>
      </div>

      {(result.parameterStatus || result.rewritten || (result.resolvedParameters?.length ?? 0) > 0 || partialMissing.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
          {result.parameterStatus && <span className="rounded-full border border-cyan-300 bg-cyan-50 px-2 py-1 text-cyan-800 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100">{result.parameterStatus}</span>}
          {result.rewritten && <span className="rounded-full border border-violet-300 bg-violet-50 px-2 py-1 text-violet-800 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-100">query rewritten</span>}
          {partialMissing.slice(0, 6).map((name) => <span key={name} className="rounded-full border border-cyan-300 bg-cyan-50 px-2 py-1 text-cyan-800 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100">union ignored: {name}</span>)}
          {(result.resolvedParameters ?? []).slice(0, 6).map((name) => <span key={name} className="rounded-full border border-slate-200 bg-white px-2 py-1 text-muted-foreground dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">{name}</span>)}
        </div>
      )}
      {(result.error || result.skippedReason) && <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-100">{result.error || result.skippedReason}</div>}
      {hasData && result.rowCount === 0 && <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 text-sm text-muted-foreground shadow-sm dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">Query executed successfully but returned no rows for the selected time range.</div>}
      {result.status === 'rendered' && (
        <div className="mt-4 space-y-4">
          {result.visualization === 'kpi' && <KpiResultGrid result={result} />}
          {(result.visualization === 'bar' || result.visualization === 'trend') && <MiniBarChart result={result} />}
          <ResultTable result={result} />
        </div>
      )}
      <details className="mt-4 rounded-xl border border-slate-200 bg-white/80 p-3 dark:border-slate-800 dark:bg-black/20">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground dark:text-slate-500">Executed KQL / preview</summary>
        <pre className="mt-3 max-h-52 overflow-auto whitespace-pre-wrap text-[11px] leading-5 text-slate-700 dark:text-slate-300">{result.executedQuery || result.queryPreview}</pre>
      </details>
    </div>
  );
}

function InteractiveWorkbookStudio({
  catalog,
  selectedDetail,
  studioPayload,
  studioMode,
  timeRange,
  customerName,
  customerId,
  customers,
  selectedCustomerId,
  onCustomerChange,
  canOpenInteractive,
  busy,
  onSelectWorkbook,
  onModeChange,
  onTimeRangeChange,
  onRender,
  onExport,
  onExportPdf,
  onAiAnalysis,
  parameterOverrides,
  parameterHints,
  showParameterEditor,
  onToggleParameterEditor,
  onParameterChange,
  onParameterClear,
  onGenerateParameterHints,
  onApplyParameterHint,
  onApplyAllParameterHints,
  parameterHintBusy,
  canExport,
  canGenerateAi,
}: {
  catalog: WorkbookCatalogItem[];
  selectedDetail: WorkbookDetailResponse | null;
  studioPayload: WorkbookRenderPayload | null;
  studioMode: 'preview' | 'json_template' | 'enhanced';
  timeRange: string;
  customerName?: string;
  customerId?: string;
  customers: CustomerOption[];
  selectedCustomerId: string;
  onCustomerChange: (id: string) => void;
  canOpenInteractive: boolean;
  busy: boolean;
  onSelectWorkbook: (id: string) => Promise<void>;
  onModeChange: (mode: 'preview' | 'json_template' | 'enhanced') => void;
  onTimeRangeChange: (range: string) => void;
  onRender: () => void;
  onExport: () => void;
  onExportPdf: () => void;
  onAiAnalysis: () => void;
  parameterOverrides: Record<string, string>;
  parameterHints: Record<string, WorkbookParameterHint>;
  showParameterEditor: boolean;
  onToggleParameterEditor: () => void;
  onParameterChange: (name: string, value: string) => void;
  onParameterClear: (name: string) => void;
  onGenerateParameterHints: () => void;
  onApplyParameterHint: (name: string) => void;
  onApplyAllParameterHints: () => void;
  parameterHintBusy: boolean;
  canExport: boolean;
  canGenerateAi: boolean;
}) {
  const modeButtons: Array<{ id: 'preview' | 'json_template' | 'enhanced'; label: string; description: string }> = [
    { id: 'preview', label: 'Live Preview', description: 'Run safe KQL and render concise visuals' },
    { id: 'json_template', label: 'JSON / Template Mode', description: 'Raw workbook JSON preview' },
    { id: 'enhanced', label: 'Enhanced KloudSecure Mode', description: 'SOC-friendly live cards, charts and tables' },
  ];

  return (
    <section className="rounded-3xl border border-cyan-400/20 bg-card p-5">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-cyan-900 dark:text-cyan-100"><LayoutDashboard className="h-4 w-4" /> Interactive Workbook Studio</div>
          <h3 className="mt-3 text-2xl font-black text-foreground">Interactive preview, JSON/template and enhanced workbook view</h3>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-muted-foreground">Select a synced workbook, choose customer/time context, then render a KloudSecure-native preview. The Studio executes safe workbook KQL blocks against the selected customer workspace, injects time context, and renders KPI cards, charts and tables. Unsupported parameter-heavy blocks are skipped with reason codes.</p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
          <select value={selectedCustomerId} onChange={(e) => onCustomerChange(e.target.value)} className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300">
            <option value="">Select customer</option>
            {customers.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
          <select value={selectedDetail?.workbook.id ?? ''} onChange={(e) => void onSelectWorkbook(e.target.value)} className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300">
            <option value="">Select workbook</option>
            {catalog.slice(0, 500).map((row) => <option key={row.id} value={row.id}>{row.displayName}</option>)}
          </select>
          <select value={timeRange} onChange={(e) => onTimeRangeChange(e.target.value)} className="h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300">
            <option value="1h">Last 1 hour</option>
            <option value="4h">Last 4 hours</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button onClick={onRender} disabled={!canOpenInteractive || !selectedDetail || busy} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Run Live Render</button>
          <button onClick={onAiAnalysis} disabled={!canGenerateAi || !selectedDetail || busy} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100 hover:bg-fuchsia-500/20 disabled:opacity-50"><Sparkles className="h-4 w-4" /> AI Analysis</button>
          <button onClick={onExportPdf} disabled={!canExport || !selectedDetail || busy} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 text-sm font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"><Download className="h-4 w-4" /> Export PDF</button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {modeButtons.map((mode) => (
          <button key={mode.id} onClick={() => onModeChange(mode.id)} className={`rounded-2xl border p-4 text-left transition hover:border-cyan-400/40 ${studioMode === mode.id ? 'border-cyan-400/60 bg-cyan-500/15' : 'border-border bg-card'}`}>
            <div className="font-bold text-foreground">{mode.label}</div>
            <div className="mt-1 text-xs text-muted-foreground">{mode.description}</div>
          </button>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <div className="grid gap-3 md:grid-cols-4">
          <div><span className="text-muted-foreground">Customer</span><div className="font-semibold text-foreground">{customerName ?? 'MSSP / global catalog'}</div></div>
          <div><span className="text-muted-foreground">Customer ID</span><div className="truncate font-mono text-xs text-muted-foreground">{customerId ?? 'not scoped'}</div></div>
          <div><span className="text-muted-foreground">Workbook</span><div className="font-semibold text-foreground">{selectedDetail?.workbook.displayName ?? 'None selected'}</div></div>
          <div><span className="text-muted-foreground">Rendered</span><div className="font-semibold text-foreground">{studioPayload ? formatDate(studioPayload.generatedAt) : 'Not rendered'}</div></div>
        </div>
      </div>

      {!selectedDetail && <div className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-100">Choose a workbook from the selector or click Studio on a catalog card.</div>}

      {selectedDetail && studioMode === 'json_template' && (
        <div className="mt-5 rounded-2xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3"><div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><FileJson className="h-4 w-4" /> JSON / Template Mode</div><button onClick={onExport} className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground"><Download className="h-4 w-4" /> Export</button></div>
          <pre className="max-h-[620px] overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-black/30 p-4 text-[11px] leading-5 text-muted-foreground">{selectedDetail.workbookJsonPreview}</pre>
        </div>
      )}

      {selectedDetail && studioMode !== 'json_template' && (
        <div className="mt-5 space-y-5">
          <LiveRuntimeOverview payload={studioPayload} />
          <ParameterResolverPanel
            parameters={studioPayload?.parameters ?? []}
            runtime={studioPayload?.runtime}
            overrides={parameterOverrides}
            hints={parameterHints}
            showEditor={showParameterEditor}
            hintBusy={parameterHintBusy}
            canGenerateAi={canGenerateAi}
            onToggleEditor={onToggleParameterEditor}
            onChange={onParameterChange}
            onClear={onParameterClear}
            onGenerateHints={onGenerateParameterHints}
            onApplyHint={onApplyParameterHint}
            onApplyAllHints={onApplyAllParameterHints}
          />

          <div className="grid gap-5 xl:grid-cols-[0.85fr_1.15fr]">
            <div className="rounded-3xl border border-border bg-gradient-to-br from-slate-900/90 via-slate-950 to-cyan-950/20 p-5">
              <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><Sparkles className="h-4 w-4" /> Enhanced Summary</div>
              <h4 className="mt-3 text-2xl font-black text-foreground">{selectedDetail.workbook.displayName}</h4>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{selectedDetail.workbook.description || 'No description detected.'}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {(studioPayload?.summaryCards ?? [
                  { label: 'Required Tables', value: selectedDetail.workbook.requiredTables.length },
                  { label: 'Query Blocks', value: selectedDetail.queryBlocks.length },
                  { label: 'Validation', value: selectedDetail.workbook.validationStatus ?? 'unknown' },
                  { label: 'Readiness', value: selectedDetail.workbook.runnableStatus ?? 'not checked' },
                ]).map((card) => <DetailMetric key={card.label} label={card.label} value={card.value} />)}
              </div>
              <div className="mt-4 grid gap-3">
                <div className="rounded-2xl border border-border bg-card p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Required tables</div>
                  <div className="mt-3 flex flex-wrap gap-2">{selectedDetail.workbook.requiredTables.slice(0, 18).map((table) => <span key={table} className={`rounded-md border px-2 py-1 font-mono text-[11px] ${tableChipClass(table, selectedDetail.workbook)}`}>{table}</span>)}</div>
                  {selectedDetail.workbook.requiredTables.length === 0 && <div className="mt-2 text-xs text-muted-foreground">No required tables detected.</div>}
                </div>
                {studioPayload?.notice && <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 p-3 text-xs text-cyan-900 dark:text-cyan-100">{studioPayload.notice}</div>}
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><Code2 className="h-4 w-4" /> Live rendered workbook visuals</div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => exportRuntimeCsv(selectedDetail.workbook.displayName, studioPayload?.queryResults ?? [])} disabled={!studioPayload?.queryResults?.length} className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-cyan-400/40 disabled:opacity-50"><Download className="h-4 w-4" /> Export CSV</button>
                  <button onClick={onExportPdf} disabled={!canExport || !selectedDetail} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"><Download className="h-4 w-4" /> Export PDF</button>
                </div>
              </div>
              <div className="mt-4 grid gap-4">
                {(studioPayload?.queryResults ?? []).map((result) => <QueryResultCard key={result.id} result={result} />)}
                {(!studioPayload?.queryResults || studioPayload.queryResults.length === 0) && (
                  <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-100">
                    No live query results are available yet. Select a customer/workspace context and click Run Live Render. If the workbook is parameter-heavy, JSON/template mode can still be used to inspect the source.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  wide,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
  wide?: boolean;
}) {
  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };
  const caption = selected.length === 0 ? `All ${label.toLowerCase()}` : `${label}: ${selected.length}`;
  return (
    <details className={`group relative ${wide ? 'min-w-[220px]' : 'min-w-[160px]'}`}>
      <summary className="flex h-10 cursor-pointer list-none items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none hover:border-cyan-300">
        <span className="truncate">{caption}</span>
        <span className="text-muted-foreground group-open:rotate-180">⌄</span>
      </summary>
      <div className="absolute right-0 z-30 mt-2 max-h-80 w-72 overflow-auto rounded-2xl border border-border bg-card p-3 shadow-2xl shadow-black/60">
        <div className="mb-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>{label}</span>
          {selected.length > 0 && <button onClick={() => onChange([])} className="text-cyan-900 dark:text-cyan-200 hover:text-cyan-900 dark:text-cyan-100">clear</button>}
        </div>
        {options.length === 0 ? <div className="text-xs text-muted-foreground">No values yet</div> : options.map((option) => (
          <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-muted-foreground hover:bg-card">
            <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} className="h-3.5 w-3.5 accent-cyan-400" />
            <span className="truncate" title={option.label}>{option.label}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function ImportWorkbookPanel({
  canImport,
  filename,
  jsonText,
  result,
  busy,
  onFilenameChange,
  onJsonTextChange,
  onFileSelected,
  onValidate,
  onSave,
}: {
  canImport: boolean;
  filename: string;
  jsonText: string;
  result: WorkbookImportResponse | null;
  busy: boolean;
  onFilenameChange: (value: string) => void;
  onJsonTextChange: (value: string) => void;
  onFileSelected: (file: File | null) => void;
  onValidate: () => void;
  onSave: () => void;
}) {
  return (
    <section className="rounded-3xl border border-cyan-400/20 bg-card p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-xl font-bold text-foreground">Import Custom Workbook JSON</h3>
          <p className="mt-1 max-w-4xl text-sm leading-6 text-muted-foreground">Upload or paste an exported Azure Workbook JSON/ARM template. KloudSecure validates it, extracts tables, tags and metadata, then saves it as an imported workbook when approved.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onValidate} disabled={!canImport || busy || !jsonText.trim()} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-50"><FileSearch className="h-4 w-4" /> Validate</button>
          <button onClick={onSave} disabled={!canImport || busy || !jsonText.trim()} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"><UploadCloud className="h-4 w-4" /> Save Imported Workbook</button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-2xl border border-border bg-card p-4">
          <label className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">File name</label>
          <input value={filename} onChange={(e) => onFilenameChange(e.target.value)} className="mt-2 h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" />
          <label className="mt-4 block text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Upload .json</label>
          <input type="file" accept=".json,.jsonc,.workbook,application/json" onChange={(e) => onFileSelected(e.target.files?.[0] ?? null)} className="mt-2 w-full rounded-xl border border-border bg-card p-2 text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-500/15 file:px-3 file:py-1 file:text-cyan-900 dark:text-cyan-100" />
          {result && (
            <div className="mt-4 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
              <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full border px-3 py-1 text-xs ${validationClass(result.validationStatus)}`}>{result.validationStatus}</span><span>{result.saved ? 'Saved' : 'Validated only'}</span><span>{result.action}</span></div>
              <div className="mt-3 font-bold text-foreground">{result.preview.displayName}</div>
              <div className="mt-2 text-xs text-muted-foreground">Tables: {result.preview.requiredTables.length ? result.preview.requiredTables.join(', ') : 'none detected'}</div>
              {result.validationErrors.length > 0 && <div className="mt-2 text-xs text-amber-800 dark:text-amber-200">{result.validationErrors.join(' ')}</div>}
            </div>
          )}
        </div>
        <textarea value={jsonText} onChange={(e) => onJsonTextChange(e.target.value)} placeholder="Paste workbook JSON or ARM template here..." rows={16} className="min-h-[360px] rounded-2xl border border-border bg-card p-4 font-mono text-xs leading-5 text-muted-foreground outline-none focus:border-cyan-300" />
      </div>
    </section>
  );
}


function AiWorkbookGeneratorPanel({
  prompt,
  mode,
  tables,
  result,
  customers,
  selectedCustomerId,
  onCustomerChange,
  tableSuggestions,
  busyKey,
  canGenerate,
  canExport,
  canDeploy,
  onPromptChange,
  onModeChange,
  onTablesChange,
  onGenerate,
  onSave,
  onExportJson,
  onOpenStudio,
  onDeploy,
}: {
  prompt: string;
  mode: string;
  tables: string;
  result: WorkbookGenerateResponse | null;
  customers: CustomerOption[];
  selectedCustomerId: string;
  onCustomerChange: (id: string) => void;
  tableSuggestions: string[];
  busyKey: string | null;
  canGenerate: boolean;
  canExport: boolean;
  canDeploy: boolean;
  onPromptChange: (value: string) => void;
  onModeChange: (value: string) => void;
  onTablesChange: (value: string) => void;
  onGenerate: () => void;
  onSave: () => void;
  onExportJson: () => void;
  onOpenStudio: () => void;
  onDeploy: () => void;
}) {
  const busy = busyKey === 'generator-generate' || busyKey === 'generator-save';
  const modeOptions = [
    { id: 'auto', label: 'Auto-detect', hint: 'Let KloudSecure infer the best workbook type' },
    { id: 'executive_soc', label: 'Executive SOC', hint: 'KPI, incident, risk and posture summary' },
    { id: 'incident_ops', label: 'Incident Ops', hint: 'Incident queue, alert mix and triage views' },
    { id: 'connector_health', label: 'Connector Health', hint: 'Usage, heartbeat and ingestion freshness' },
    { id: 'threat_hunting', label: 'Threat Hunting', hint: 'MITRE, entity and Defender hunting views' },
    { id: 'vulnerability', label: 'Vulnerability', hint: 'CVE and exposure-oriented workbook' },
    { id: 'threat_intel', label: 'Threat Intel', hint: 'IoC, TI hit and indicator views' },
  ];
  const draft = result?.draft;
  const suggestionText = tableSuggestions.slice(0, 12).join(', ');
  const layout = draft?.layout ?? [];
  const queries = draft?.kqlBlocks ?? [];

  return (
    <section className="space-y-5">
      <div className="overflow-hidden rounded-3xl border border-fuchsia-400/25 bg-card shadow-2xl shadow-fuchsia-950/10">
        <div className="relative p-6">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(217,70,239,0.18),transparent_34%),radial-gradient(circle_at_top_right,rgba(34,211,238,0.14),transparent_28%)]" />
          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-900 dark:text-fuchsia-100"><Wand2 className="h-4 w-4" /> AI Workbook Generator</div>
              <h3 className="mt-3 max-w-3xl text-3xl font-black text-foreground">Prompt-to-KQL, layout and workbook JSON generation</h3>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-muted-foreground">Generate a professional KloudSecure workbook from a SOC use case, preview the generated KQL and layout, save it as an AI-generated catalog workbook, export JSON, open it in Studio or move directly to the deployment wizard.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-border/80 bg-card p-4"><div className="text-2xl font-black text-foreground">{draft?.kqlBlocks.length ?? 0}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">KQL blocks</div></div>
              <div className="rounded-2xl border border-border/80 bg-card p-4"><div className="text-2xl font-black text-foreground">{draft?.requiredTables.length ?? 0}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">Tables</div></div>
              <div className="rounded-2xl border border-border/80 bg-card p-4"><div className="text-2xl font-black text-foreground">{draft?.layout.length ?? 0}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">Layout blocks</div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-3xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-muted-foreground"><Sparkles className="h-4 w-4 text-fuchsia-900 dark:text-fuchsia-200" /> Generator Prompt Console</div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Field label="Customer context"><select value={selectedCustomerId} onChange={(e) => onCustomerChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-fuchsia-300"><option value="">No customer context</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></Field>
            <Field label="Workbook mode"><select value={mode} onChange={(e) => onModeChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-fuchsia-300">{modeOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></Field>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {modeOptions.filter((option) => option.id !== 'auto').map((option) => <button key={option.id} onClick={() => onModeChange(option.id)} className={`rounded-2xl border p-3 text-left text-xs transition ${mode === option.id ? 'border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-50' : 'border-border bg-card text-muted-foreground hover:border-slate-600'}`}><div className="font-bold text-foreground">{option.label}</div><div className="mt-1 leading-5">{option.hint}</div></button>)}
          </div>
          <label className="mt-4 block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Prompt</span>
            <textarea value={prompt} onChange={(e) => onPromptChange(e.target.value)} rows={7} className="mt-2 w-full rounded-2xl border border-border bg-card p-4 text-sm leading-6 text-foreground outline-none focus:border-fuchsia-300" placeholder="Example: Create an executive SOC workbook for incidents, SLA, connector health, TI hits and top risky users/devices." />
          </label>
          <label className="mt-4 block">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Optional required tables</span>
            <input value={tables} onChange={(e) => onTablesChange(e.target.value)} className="mt-2 h-11 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-fuchsia-300" placeholder={suggestionText || 'SecurityIncident, SecurityAlert, Usage'} />
            <span className="mt-2 block text-xs text-muted-foreground">Comma-separated. Leave empty to let KloudSecure infer tables from the prompt.</span>
          </label>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={onGenerate} disabled={!canGenerate || busy} className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-2 text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100 hover:bg-fuchsia-500/20 disabled:opacity-50"><Sparkles className={`h-4 w-4 ${busyKey === 'generator-generate' ? 'animate-pulse' : ''}`} /> Generate draft</button>
            <button onClick={onSave} disabled={!canGenerate || busy || !draft} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-50"><BookOpenCheck className="h-4 w-4" /> Save to catalog</button>
            <button onClick={onExportJson} disabled={!canExport || !draft} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-50"><Download className="h-4 w-4" /> Export JSON</button>
            <button onClick={onOpenStudio} disabled={!canGenerate || busy || !draft} className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-muted px-4 py-2 text-sm font-semibold text-foreground hover:bg-slate-700 disabled:opacity-50"><LayoutDashboard className="h-4 w-4" /> Open Studio</button>
            <button onClick={onDeploy} disabled={!canDeploy || busy || !draft} className="inline-flex items-center gap-2 rounded-xl border border-violet-400/30 bg-violet-500/10 px-4 py-2 text-sm font-semibold text-violet-900 dark:text-violet-100 hover:bg-violet-500/20 disabled:opacity-50"><Rocket className="h-4 w-4" /> Deploy path</button>
          </div>
        </div>

        <div className="rounded-3xl border border-cyan-400/20 bg-card p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-900 dark:text-cyan-200">Generated Workbook Preview</div>
              <h3 className="mt-2 text-2xl font-black text-foreground">{draft?.displayName ?? 'No draft generated yet'}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{draft?.description ?? 'Enter a prompt and generate a workbook to see KQL blocks, layout, JSON and AI improvement suggestions.'}</p>
            </div>
            {result && <span className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-1 text-xs font-semibold text-fuchsia-900 dark:text-fuchsia-100">{result.aiProvider} · {result.model}</span>}
          </div>
          {!draft ? (
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {['Prompt generator', 'KQL generation', 'Layout generation', 'JSON generation', 'Save / export / deploy', 'AI explanation'].map((item) => <div key={item} className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground"><CheckCircle2 className="mb-3 h-4 w-4 text-emerald-800 dark:text-emerald-300" />{item}</div>)}
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-border bg-card p-4"><div className="text-2xl font-black text-foreground">{draft.requiredTables.length}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">Required tables</div></div>
                <div className="rounded-2xl border border-border bg-card p-4"><div className="text-2xl font-black text-foreground">{draft.kqlBlocks.length}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">KQL blocks</div></div>
                <div className="rounded-2xl border border-border bg-card p-4"><div className="text-2xl font-black text-foreground">{draft.layout.length}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">Layout sections</div></div>
                <div className="rounded-2xl border border-border bg-card p-4"><div className="text-2xl font-black text-foreground">{result?.saved ? 'Saved' : 'Draft'}</div><div className="text-xs uppercase tracking-wide text-muted-foreground">Catalog state</div></div>
              </div>
              <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/10 p-4"><div className="text-sm font-bold text-fuchsia-900 dark:text-fuchsia-100">AI explanation</div><p className="mt-2 text-sm leading-6 text-foreground">{draft.explanation}</p></div>
              <div className="flex flex-wrap gap-2">{draft.requiredTables.map((table) => <span key={table} className="rounded-md border border-cyan-400/20 bg-cyan-500/10 px-2 py-1 font-mono text-xs text-cyan-900 dark:text-cyan-100">{table}</span>)}</div>
              <div className="grid gap-4 xl:grid-cols-2">
                <div className="rounded-2xl border border-border bg-card p-4"><div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Generated layout</div>{layout.map((section) => <div key={String(section.id ?? section.title)} className="mb-2 rounded-xl border border-border bg-card p-3"><div className="font-semibold text-foreground">{String(section.title ?? section.id)}</div><div className="text-xs text-muted-foreground">{String(section.type ?? 'section')}</div></div>)}</div>
                <div className="rounded-2xl border border-border bg-card p-4"><div className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Improvement suggestions</div><ul className="space-y-2 text-sm text-muted-foreground">{draft.improvements.map((item) => <li key={item} className="rounded-xl border border-border bg-card p-3">{item}</li>)}</ul></div>
              </div>
              <div className="space-y-3">
                {queries.slice(0, 8).map((query) => <div key={query.id} className="rounded-2xl border border-border bg-card p-4"><div className="flex flex-wrap items-center gap-2"><h4 className="font-bold text-foreground">{query.title}</h4><span className="rounded-full border border-border bg-card px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">{query.visualization}</span><span className="rounded-md border border-cyan-400/20 bg-cyan-500/10 px-2 py-1 font-mono text-[11px] text-cyan-900 dark:text-cyan-100">{query.table}</span></div><p className="mt-2 text-xs text-muted-foreground">{query.purpose}</p><pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-black/30 p-3 text-[11px] leading-5 text-muted-foreground">{query.kql}</pre></div>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function DeploymentWizardPanel({
  catalog,
  customers,
  deployments,
  selectedWorkbookId,
  selectedCustomerId,
  subscriptionId,
  resourceGroup,
  workspaceName,
  location,
  deploymentName,
  enableAiAnalysis,
  confirmDeploy,
  plan,
  busyKey,
  canDeploy,
  canExport,
  canRollback,
  onWorkbookChange,
  onCustomerChange,
  onSubscriptionChange,
  onResourceGroupChange,
  onWorkspaceNameChange,
  onLocationChange,
  onDeploymentNameChange,
  onEnableAiChange,
  onConfirmChange,
  onGeneratePlan,
  onExportArm,
  onDeploy,
  onRollback,
}: {
  catalog: WorkbookCatalogItem[];
  customers: CustomerOption[];
  deployments: WorkbookDeploymentItem[];
  selectedWorkbookId: string;
  selectedCustomerId: string;
  subscriptionId: string;
  resourceGroup: string;
  workspaceName: string;
  location: string;
  deploymentName: string;
  enableAiAnalysis: boolean;
  confirmDeploy: boolean;
  plan: WorkbookDeploymentPlanResponse | null;
  busyKey: string | null;
  canDeploy: boolean;
  canExport: boolean;
  canRollback: boolean;
  onWorkbookChange: (id: string) => void;
  onCustomerChange: (id: string) => void;
  onSubscriptionChange: (value: string) => void;
  onResourceGroupChange: (value: string) => void;
  onWorkspaceNameChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onDeploymentNameChange: (value: string) => void;
  onEnableAiChange: (value: boolean) => void;
  onConfirmChange: (value: boolean) => void;
  onGeneratePlan: () => void;
  onExportArm: () => void;
  onDeploy: () => void;
  onRollback: (id: string) => void;
}) {
  function exportDeploymentHistoryCsv(): void {
    const headers = ['Deployment Name', 'Workbook ID', 'Customer ID', 'Subscription ID', 'Resource Group', 'Workspace', 'Status', 'Azure Resource ID', 'Deployed By', 'Deployed At', 'Last Error'];
    const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = deployments.map((row) => [row.deploymentName || row.id, row.workbookId, row.customerId, row.subscriptionId ?? '', row.resourceGroup, row.workspaceName, row.deploymentStatus, row.azureResourceId ?? '', row.deployedByEmail, row.deployedAt, row.lastError]);
    const blob = new Blob([[headers, ...rows].map((r) => r.map(escapeCsv).join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kloudsecure-workbook-deployments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="space-y-5">
      <div className="rounded-3xl border border-cyan-400/20 bg-card p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-xl font-bold text-foreground">Workbook Deployment Wizard</h3>
            <p className="mt-1 max-w-4xl text-sm leading-6 text-muted-foreground">Inject customer/workspace context, validate required tables, export ARM, then deploy or update the Azure Workbook resource. Deployment requires explicit confirmation.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onGeneratePlan} disabled={!canDeploy || busyKey === 'deployment-plan'} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 disabled:opacity-50"><FileSearch className="h-4 w-4" /> Generate Plan</button>
            <button onClick={onExportArm} disabled={!canExport || busyKey === 'arm-export'} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-800 dark:text-emerald-100 disabled:opacity-50"><Download className="h-4 w-4" /> Export ARM</button>
            <button onClick={onDeploy} disabled={!canDeploy || busyKey === 'workbook-deploy'} className="inline-flex items-center gap-2 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-2 text-sm font-semibold text-fuchsia-900 dark:text-fuchsia-100 disabled:opacity-50"><Rocket className="h-4 w-4" /> Deploy / Update</button>
          </div>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          <Field label="Workbook"><select value={selectedWorkbookId} onChange={(e) => onWorkbookChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300"><option value="">Select workbook</option>{catalog.map((row) => <option key={row.id} value={row.id}>{row.displayName}</option>)}</select></Field>
          <Field label="Customer"><select value={selectedCustomerId} onChange={(e) => onCustomerChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300"><option value="">Select customer</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Deployment/resource name"><input value={deploymentName} onChange={(e) => onDeploymentNameChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" placeholder="ks-workbook-name" /></Field>
          <Field label="Subscription ID"><input value={subscriptionId} onChange={(e) => onSubscriptionChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" /></Field>
          <Field label="Resource group"><input value={resourceGroup} onChange={(e) => onResourceGroupChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" /></Field>
          <Field label="Workspace name"><input value={workspaceName} onChange={(e) => onWorkspaceNameChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" /></Field>
          <Field label="Location"><input value={location} onChange={(e) => onLocationChange(e.target.value)} className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm text-foreground outline-none focus:border-cyan-300" /></Field>
          <label className="flex min-h-[68px] items-center gap-3 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground"><input type="checkbox" checked={enableAiAnalysis} onChange={(e) => onEnableAiChange(e.target.checked)} className="h-4 w-4 accent-fuchsia-400" />Enable AI Analysis parameter</label>
          <label className="flex min-h-[68px] items-center gap-3 rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-800 dark:text-red-100"><input type="checkbox" checked={confirmDeploy} onChange={(e) => onConfirmChange(e.target.checked)} className="h-4 w-4 accent-red-400" />Confirm Azure deploy/update</label>
        </div>
        {plan && (
          <div className="mt-5 grid gap-4 xl:grid-cols-[0.8fr_1.2fr]">
            <div className={`rounded-2xl border p-4 text-sm ${plan.validation.ok ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100' : 'border-red-400/30 bg-red-500/10 text-red-800 dark:text-red-100'}`}>
              <div className="font-bold">Validation: {plan.validation.ok ? 'Ready' : 'Blocked'}</div>
              {plan.validation.errors.length > 0 && <ul className="mt-2 list-disc pl-5">{plan.validation.errors.map((err) => <li key={err}>{err}</li>)}</ul>}
              {plan.validation.warnings.length > 0 && <ul className="mt-2 list-disc pl-5 text-amber-800 dark:text-amber-100">{plan.validation.warnings.map((warn) => <li key={warn}>{warn}</li>)}</ul>}
              {plan.resourceId && <div className="mt-3 break-all font-mono text-xs opacity-80">{plan.resourceId}</div>}
            </div>
            <pre className="max-h-72 overflow-auto rounded-2xl border border-border bg-black/30 p-4 text-[11px] leading-5 text-muted-foreground">{JSON.stringify(plan.armTemplate ?? plan, null, 2)}</pre>
          </div>
        )}
      </div>
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center justify-between gap-3"><div><h3 className="text-xl font-bold text-foreground">Deployment History</h3><p className="mt-1 text-sm text-muted-foreground">Tracks Azure workbook deployments, failures and rollback availability.</p></div><button onClick={exportDeploymentHistoryCsv} className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-100"><Download className="h-4 w-4" /> Export CSV</button></div>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-left text-sm"><thead className="bg-card text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="p-3">Workbook</th><th className="p-3">Customer</th><th className="p-3">Workspace</th><th className="p-3">Status</th><th className="p-3">Time</th><th className="p-3">Actions</th></tr></thead><tbody className="divide-y divide-slate-800">
            {deployments.length === 0 ? <tr><td className="p-4 text-muted-foreground" colSpan={6}>No workbook deployments yet.</td></tr> : deployments.map((row) => (
              <tr key={row.id} className="bg-card"><td className="p-3"><div className="font-mono text-xs text-muted-foreground">{row.deploymentName || row.workbookId || '-'}</div></td><td className="p-3">{row.customerId || '-'}</td><td className="p-3">{row.workspaceName || '-'}</td><td className="p-3"><span className={`rounded-full border px-2 py-1 text-xs ${row.deploymentStatus === 'deployed' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-100' : row.deploymentStatus.includes('failed') ? 'border-red-400/30 bg-red-500/10 text-red-800 dark:text-red-100' : 'border-amber-400/30 bg-amber-500/10 text-amber-800 dark:text-amber-100'}`}>{row.deploymentStatus}</span></td><td className="p-3">{formatDate(row.deployedAt)}</td><td className="p-3"><button onClick={() => onRollback(row.id)} disabled={!canRollback || !row.rollbackAvailable || busyKey === `rollback-${row.id}`} className="rounded-lg border border-border bg-card px-3 py-1 text-xs text-muted-foreground disabled:opacity-40">Rollback</button></td></tr>
            ))}
          </tbody></table>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block rounded-2xl border border-border bg-card p-4 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground"><span>{label}</span><div className="mt-2 normal-case tracking-normal">{children}</div></label>;
}


function SourceSyncPanel({
  icon,
  title,
  description,
  roots,
  canSync,
  busy,
  actionLabel,
  onSync,
  bullets,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  roots: string[];
  canSync: boolean;
  busy: boolean;
  actionLabel: string;
  onSync: () => void;
  bullets: string[];
}) {
  return (
    <section className="rounded-3xl border border-border bg-card p-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-3 text-cyan-900 dark:text-cyan-100">{icon}</div>
          <div>
            <h3 className="text-xl font-bold text-foreground">{title}</h3>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p>
          </div>
        </div>
        <button onClick={onSync} disabled={!canSync || busy} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> {busy ? 'Syncing…' : actionLabel}
        </button>
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {bullets.map((bullet) => (
          <div key={bullet} className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            <CheckCircle2 className="mb-3 h-4 w-4 text-emerald-800 dark:text-emerald-300" />{bullet}
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl border border-border bg-card p-4">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Configured source roots</div>
        <div className="mt-3 grid gap-2">
          {roots.length === 0 ? <div className="text-sm text-muted-foreground">Source roots will appear here after summary loads.</div> : roots.map((root) => (
            <code key={root} className="block overflow-x-auto rounded-xl border border-border bg-card px-3 py-2 text-xs text-muted-foreground">{root}</code>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanningPanel({
  icon,
  title,
  description,
  actions,
  bullets,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  actions: Array<{ label: string; enabled: boolean; busy: boolean; onClick: () => void }>;
  bullets: string[];
}) {
  return (
    <section className="rounded-3xl border border-border bg-card p-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-3 text-cyan-900 dark:text-cyan-100">{icon}</div>
          <div>
            <h3 className="text-xl font-bold text-foreground">{title}</h3>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => (
            <button key={action.label} onClick={action.onClick} disabled={!action.enabled || action.busy} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-900 dark:text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${action.busy ? 'animate-spin' : ''}`} /> {action.busy ? 'Recording…' : action.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {bullets.map((bullet) => (
          <div key={bullet} className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            <CheckCircle2 className="mb-3 h-4 w-4 text-emerald-800 dark:text-emerald-300" />{bullet}
          </div>
        ))}
      </div>
    </section>
  );
}
